import React, { Fragment, useState, useEffect } from "react";
import { showHttpError, showToast, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, CURRENCIES, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";

// ─────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────
const API_BASE = WEBSERVICE;
const getMid = () => localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
  UUID: getAuthUuid() || "",
});
const apiReq = (url, opts = {}) =>
  fetch(url, { headers: authHeaders(), ...opts }).then((r) => {
    if (!r.ok) throw { response: { status: r.status } };
    return r.json();
  });

const apiGetCatalogs = (mid) => apiReq(`${API_BASE}/shopping/catalogs/${mid}`);
const apiCreateCatalog = (body) =>
  apiReq(`${API_BASE}/shopping/catalogs`, {
    method: "POST",
    body: JSON.stringify(body),
  });
const apiUpdateCatalog = (id, body) =>
  apiReq(`${API_BASE}/shopping/catalogs/updatecatalogs/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
const apiDeleteCatalog = (id, mid) =>
  apiReq(`${API_BASE}/shopping/catalogs/deletecatalogs/${id}/${mid}`, { method: "DELETE" });
const apiGetCatalogProducts = (mid, catalogId) =>
  apiReq(`${API_BASE}/shopping/products/${mid}/${catalogId}`);
const apiAddProductToCart = (catalogId, productId, priceId, action = "ADD") =>
  apiReq(
    `${API_BASE}/shopping/catalogs/add-product-price?catalogId=${catalogId}&productId=${productId}&priceId=${priceId}&action=${action}`,
    { method: "POST" }
  ); 

const apiRegisterProducts = (body) =>
  apiReq(`${API_BASE}/shopping/products/register`, {
    method: "POST",
    body: JSON.stringify(body),
  });
const apiUpdateProduct = (productId, body) =>
  apiReq(`${API_BASE}/shopping/products?productId=${productId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
const apiDeleteProduct = (merchantId, productId) =>
  apiReq(
    `${API_BASE}/shopping/products?merchantId=${merchantId}&productId=${productId}`,
    { method: "DELETE" }
  );
const apiUploadProductImage = (merchantId, productId, imageFile) => {
  const formData = new FormData();
  formData.append("merchantId", merchantId);
  formData.append("productId", productId);
  formData.append("image", imageFile);
  return fetch(`${API_BASE}/shopping/products/image`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      UUID: getAuthUuid() || "",
    },
    body: formData,
  }).then((r) => {
    if (!r.ok) throw { response: { status: r.status } };
    return r.json();
  });
};

// ─────────────────────────────────────────────────────────────────
// Payload builders
// ─────────────────────────────────────────────────────────────────
function buildProductPayload(merchantId, form) {
  if (form.status === "Draft") {
    const product = {
      productId: form.productId || `PROD_${merchantId}_${Date.now()}`,
      productType: "PAYMENT_LINK",
      name: form.name,
      status: "DRAFT",
    };
    if (form.description && form.description.trim()) product.description = form.description;
    if (form.imageFile) product.imageUrl = null;
    else if (form.imageUrl && form.imageUrl.trim()) product.imageUrl = form.imageUrl;
    const dAttrs = {};
    (form.variants || []).forEach((v) => { if (v.name && v.name.trim()) dAttrs[v.name.trim()] = v.values || []; });
    if (Object.keys(dAttrs).length) product.attributes = dAttrs;
    const dMeta = {};
    (form.attrs || []).forEach((a) => { if (a.key && a.key.trim()) dMeta[a.key.trim()] = a.val; });
    if (Object.keys(dMeta).length) product.metadata = dMeta;
    const filledPrices = (form.prices || []).filter((p) => p.amount && String(p.amount).trim() !== "");
    if (filledPrices.length) {
      product.prices = filledPrices.map((p) => {
        const anyExtraDefault = (p.extraCurrencies || []).some((ec) => ec.isDefault);
        const currencies = [
          { currency: p.currency || "USD", amount: parseFloat(p.amount) || 0, isDefault: !anyExtraDefault },
          ...(p.extraCurrencies || []).map((ec) => ({ currency: ec.currency, amount: parseFloat(ec.amount) || 0, isDefault: !!ec.isDefault })),
        ];
        const isSub = p.priceType === "subscription";
        return {
          isDefault: !!p.isDefault,
          priceType: isSub ? "recurring" : "one-time",
          intervalType: isSub ? p.billingInterval || "Month" : null,
          intervalCount: isSub ? parseInt(p.billingEvery || "1") : null,
          trialDays: p.trialDays ? parseInt(p.trialDays) : 0,
          totalCycles: (() => {
            if (!isSub) return "0";
            if ((p.billingDuration || "no-end") === "fixed") return parseInt(p.totalCycles || "0") || "0";
            const intervalMap = { Week: 5200, Month: 1200, Year: 100 };
            return intervalMap[p.billingInterval || "Month"] ?? 1200;
          })(),
          retryAttempts: isSub ? parseInt(p.retryAttempts || "3") : null,
          retryInterval: isSub ? p.retryInterval || "Day" : null,
          variant: p.variantObj || {},
          sku: p.sku || "",
          inventory: { track: !!p.trackInventory, quantity: p.trackInventory ? parseInt(p.inventoryQty) || 0 : 0 },
          currencies,
        };
      });
    }
    return { merchantId: parseInt(merchantId), products: [product] };
  }
  const attributes = {};
  (form.variants || []).forEach((v) => {
    if (v.name.trim()) attributes[v.name.trim()] = v.values || [];
  });
  const metadata = {};
  (form.attrs || []).forEach((a) => {
    if (a.key.trim()) metadata[a.key.trim()] = a.val;
  });
  const prices = (form.prices || []).map((p) => {
    const anyExtraDefault = (p.extraCurrencies || []).some((ec) => ec.isDefault);
    const currencies = [
      {
        currency: p.currency || "USD",
        amount: parseFloat(p.amount) || 0,
        isDefault: !anyExtraDefault,
      },
      ...(p.extraCurrencies || []).map((ec) => ({
        currency: ec.currency,
        amount: parseFloat(ec.amount) || 0,
        isDefault: !!ec.isDefault,
      })),
    ];
    const isSub = p.priceType === "subscription";
    return {
      isDefault: !!p.isDefault,
      priceType: isSub ? "recurring" : "one-time",
      intervalType: isSub ? p.billingInterval || "Month" : null,
      intervalCount: isSub ? parseInt(p.billingEvery || "1") : null,
      trialDays: p.trialDays ? parseInt(p.trialDays) : 0,
      totalCycles: (() => {
        if (!isSub) return "0";
        if ((p.billingDuration || "no-end") === "fixed")
          return parseInt(p.totalCycles || "0") || "0";
        const intervalMap = { Week: 5200, Month: 1200, Year: 100 };
        return intervalMap[p.billingInterval || "Month"] ?? 1200;
      })(),
      retryAttempts: isSub ? parseInt(p.retryAttempts || "3") : null,
      retryInterval: isSub ? p.retryInterval || "Day" : null,
      variant: p.variantObj || {},
      sku: p.sku || "",
      inventory: {
        track: !!p.trackInventory,
        quantity: p.trackInventory ? parseInt(p.inventoryQty) || 0 : 0,
      },
      currencies,
    };
  });
  const statusMap = { Active: "ACTIVE", Draft: "DRAFT", Archived: "ARCHIVED" };
  return {
    merchantId: parseInt(merchantId),
    products: [
      {
        productId: form.productId || `PROD_${Date.now()}`,
        productType: "PAYMENT_LINK",
        name: form.name,
        status: statusMap[form.status] || "ACTIVE",
        description: form.description || "",
        imageUrl: form.imageFile ? null : (form.imageUrl || ""),
        attributes,
        metadata,
        prices,
      },
    ],
  };
}

function buildUpdatePayload(merchantId, form) {
  if (form.status === "Draft") {
    const payload = {
      merchantId: parseInt(merchantId),
      name: form.name,
      productType: "PAYMENT_LINK",
      status: "DRAFT",
    };
    if (form.description && form.description.trim()) payload.description = form.description;
    if (form.imageFile) payload.imageUrl = null;
    else if (form.imageUrl && form.imageUrl.trim()) payload.imageUrl = form.imageUrl;
    const dAttrs = {};
    (form.variants || []).forEach((v) => { if (v.name && v.name.trim()) dAttrs[v.name.trim()] = v.values || []; });
    if (Object.keys(dAttrs).length) payload.attributes = dAttrs;
    const dMeta = {};
    (form.attrs || []).forEach((a) => { if (a.key && a.key.trim()) dMeta[a.key.trim()] = a.val; });
    if (Object.keys(dMeta).length) payload.metadata = dMeta;
    const filledPrices = (form.prices || []).filter((p) => p.amount && String(p.amount).trim() !== "");
    if (filledPrices.length) {
      payload.addPrices = filledPrices.map((p) => {
        const anyExtraDefault = (p.extraCurrencies || []).some((ec) => ec.isDefault);
        const currencies = [
          { currency: p.currency || "USD", amount: parseFloat(p.amount) || 0, isDefault: !anyExtraDefault },
          ...(p.extraCurrencies || []).map((ec) => ({ currency: ec.currency, amount: parseFloat(ec.amount) || 0, isDefault: !!ec.isDefault })),
        ];
        const isSub = p.priceType === "subscription";
        return {
          priceId: p.id?.toString().startsWith("price_") ? parseInt(p.id.replace("price_", "")) : undefined,
          isDefault: !!p.isDefault,
          priceType: isSub ? "recurring" : "one-time",
          intervalType: isSub ? p.billingInterval || "Month" : null,
          intervalCount: isSub ? parseInt(p.billingEvery || "1") : null,
          trialDays: p.trialDays ? parseInt(p.trialDays) : 0,
          totalCycles: (() => {
            if (!isSub) return "0";
            if ((p.billingDuration || "no-end") === "fixed") return parseInt(p.totalCycles || "0") || "0";
            const intervalMap = { Week: 5200, Month: 1200, Year: 100 };
            return intervalMap[p.billingInterval || "Month"] ?? 1200;
          })(),
          retryAttempts: isSub ? parseInt(p.retryAttempts || "3") : null,
          retryInterval: isSub ? p.retryInterval || "Day" : null,
          variant: p.variantObj || {},
          sku: p.sku || "",
          inventory: { track: !!p.trackInventory, quantity: p.trackInventory ? parseInt(p.inventoryQty) || 0 : 0 },
          currencies,
        };
      });
    }
    if (form.removeCurrenciesWithPriceId && form.removeCurrenciesWithPriceId.length > 0) {
      payload.removeCurrenciesWithPriceId = form.removeCurrenciesWithPriceId;
    }
    return payload;
  }
  const attributes = {};
  (form.variants || []).forEach((v) => {
    if (v.name.trim()) attributes[v.name.trim()] = v.values || [];
  });
  const metadata = {};
  (form.attrs || []).forEach((a) => {
    if (a.key.trim()) metadata[a.key.trim()] = a.val;
  });
  const statusMap = { Active: "ACTIVE", Draft: "DRAFT", Archived: "ARCHIVED" };
  const addPrices = (form.prices || []).map((p) => {
    const anyExtraDefault = (p.extraCurrencies || []).some((ec) => ec.isDefault);
    const currencies = [
      {
        currency: p.currency || "USD",
        amount: parseFloat(p.amount) || 0,
        isDefault: !anyExtraDefault,
      },
      ...(p.extraCurrencies || []).map((ec) => ({
        currency: ec.currency,
        amount: parseFloat(ec.amount) || 0,
        isDefault: !!ec.isDefault,
      })),
    ];
    const isSub = p.priceType === "subscription";
    return {
      priceId: p.id?.toString().startsWith("price_")
        ? parseInt(p.id.replace("price_", ""))
        : undefined,
      isDefault: !!p.isDefault,
      priceType: isSub ? "recurring" : "one-time",
      intervalType: isSub ? p.billingInterval || "Month" : null,
      intervalCount: isSub ? parseInt(p.billingEvery || "1") : null,
      trialDays: p.trialDays ? parseInt(p.trialDays) : 0,
      totalCycles: (() => {
        if (!isSub) return "0";
        if ((p.billingDuration || "no-end") === "fixed")
          return parseInt(p.totalCycles || "0") || "0";
        const intervalMap = { Week: 5200, Month: 1200, Year: 100 };
        return intervalMap[p.billingInterval || "Month"] ?? 1200;
      })(),
      retryAttempts: isSub ? parseInt(p.retryAttempts || "3") : null,
      retryInterval: isSub ? p.retryInterval || "Day" : null,
      variant: p.variantObj || {},
      sku: p.sku || "",
      inventory: {
        track: !!p.trackInventory,
        quantity: p.trackInventory ? parseInt(p.inventoryQty) || 0 : 0,
      },
      currencies,
    };
  });
  const payload = {
    merchantId: parseInt(merchantId),
    name: form.name,
    productType: "PAYMENT_LINK",
    description: form.description || "",
    imageUrl: form.imageFile ? null : (form.imageUrl || ""),
    attributes,
    metadata,
    status: statusMap[form.status] || "ACTIVE",
    addPrices,
  };
  if (form.removeCurrenciesWithPriceId && form.removeCurrenciesWithPriceId.length > 0) {
    payload.removeCurrenciesWithPriceId = form.removeCurrenciesWithPriceId;
  }
  return payload;
}

// ─────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
.pc-page { padding:32px 24px 80px;max-width:100%;overflow-x:hidden; }
.pc-hdr { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:24px;gap:16px;flex-wrap:wrap; }
.pc-title { font-size:22px;font-weight:700;letter-spacing:-.4px;color:var(--text-color);margin:0; }
.pc-back-link { display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;cursor:pointer;margin-bottom:20px;user-select:none;opacity:.85;transition:opacity .15s;text-decoration:none;color:#fff; }
.pc-back-link:hover { opacity:1;text-decoration:underline; }
[data-theme="light"] .pc-back-link, .light-theme .pc-back-link, body.light .pc-back-link { color:var(--btn-color) !important; }
.pc-sub { font-size:13px;color:var(--text-color);opacity:.65;margin:5px 0 0; }
.pc-btn { height:36px;padding:0 16px;border-radius:8px;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;display:inline-flex;align-items:center;gap:7px;transition:all .15s;white-space:nowrap;border:none; }
.pc-btn-primary { background:var(--btn-color);color:#fff;box-shadow:0 0 14px color-mix(in srgb,var(--btn-color) 30%,transparent); }
.pc-btn-primary:hover { filter:brightness(1.1);transform:scale(.98); }
.pc-btn-secondary { background:var(--body-color-less);border:1px solid var(--body-color-less2);color:var(--text-color); }
.pc-btn-secondary:hover { border-color:var(--btn-color);color:var(--btn-color); }
.pc-btn-sm { height:30px;padding:0 12px;font-size:12px;border-radius:6px; }
.pc-btn-danger { background:transparent;border:1.5px solid rgba(239,68,68,.35);color:#f87171;height:32px;padding:0 12px;font-size:12px;border-radius:7px;font-family:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:all .15s; }
.pc-btn-danger:hover { background:rgba(239,68,68,.07);border-color:#ef4444; }
.pc-btn-outline { background:transparent;border:1.5px solid var(--btn-color);color:var(--btn-color);height:32px;padding:0 12px;font-size:12px;border-radius:7px;font-family:inherit;cursor:pointer;display:inline-flex;align-items:center;gap:5px;transition:all .15s; }
.pc-btn-outline:hover { background:color-mix(in srgb,var(--btn-color) 9%,transparent); }
.pc-btn-cancel { height:40px;padding:0 20px;border-radius:8px;border:1px solid var(--body-color-less2);background:transparent;color:var(--text-color);opacity:.8;font-family:inherit;font-size:14px;font-weight:500;cursor:pointer;transition:all .15s; }
.pc-btn-cancel:hover { opacity:1;border-color:var(--text-color); }
.pc-btn-save { height:40px;padding:0 24px;border-radius:8px;border:none;background:var(--btn-color);color:#fff;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;box-shadow:0 0 14px color-mix(in srgb,var(--btn-color) 30%,transparent);transition:filter .15s,transform .15s; }
.pc-btn-save:hover { filter:brightness(1.1);transform:scale(.98); }
.pc-btn-save:disabled { opacity:.6;cursor:not-allowed;transform:none; }
.pc-topbar { display:flex;gap:10px;align-items:center;margin-bottom:16px;flex-wrap:wrap; }
.pc-search-wrap { flex:1;min-width:180px;position:relative; }
.pc-search-icon { position:absolute;left:11px;top:50%;transform:translateY(-50%);color:var(--text-color);opacity:.5;pointer-events:none; }
.pc-search-input { width:100%;height:36px;padding:0 12px 0 34px;background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:8px;color:var(--text-color);font-family:inherit;font-size:13px;outline:none;transition:border-color .2s; }
.pc-search-input::placeholder { color:var(--text-color);opacity:.5; }
.pc-search-input:focus { border-color:var(--btn-color);box-shadow:0 0 0 3px color-mix(in srgb,var(--btn-color) 12%,transparent); }
.pc-filter-sel { height:36px;padding:0 12px;background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:8px;color:var(--text-color);font-family:inherit;font-size:13px;appearance:auto;cursor:pointer;outline:none; }
.pc-filter-sel:focus { border-color:var(--btn-color); }
.pc-tbl-wrap { background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;overflow-x:auto;-webkit-overflow-scrolling:touch; }
.pc-tbl-wrap table { width:100%;border-collapse:collapse;min-width:600px; }
.pc-tbl-wrap thead tr { border-bottom:1px solid var(--body-color-less2); }
.pc-tbl-wrap thead th { padding:12px 16px;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.65px;color:var(--text-color);opacity:.7;text-align:left; }
.pc-tbl-wrap tbody tr { border-bottom:1px solid var(--body-color-less2);cursor:pointer;transition:background .15s; }
.pc-tbl-wrap tbody tr:last-child { border-bottom:none; }
.pc-tbl-wrap tbody tr:hover { background:color-mix(in srgb,var(--btn-color) 4%,transparent); }
.pc-tbl-wrap tbody td { padding:13px 16px;font-size:13px;vertical-align:middle; }
.pc-td-name { font-weight:600;color:var(--text-color); }
.pc-td-sub { font-size:11px;color:var(--text-color);opacity:.65;margin-top:2px; }
.pc-td-actions { display:flex;gap:7px;align-items:center; }
.pc-badge { display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:20px;font-size:11px;font-weight:600; }
.pc-badge::before { content:'';width:5px;height:5px;border-radius:50%;flex-shrink:0; }
.pc-badge.active { background:rgba(16,185,129,.15);color:#10b981; }
.pc-badge.active::before { background:#10b981; }
.pc-badge.draft { background:rgba(100,116,139,.15);color:#94a3b8; }
.pc-badge.draft::before { background:#94a3b8; }
.pc-badge.archived { background:rgba(239,68,68,.1);color:#f87171; }
.pc-badge.archived::before { background:#f87171; }
.pc-stock { display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600; }
.pc-empty { display:flex;flex-direction:column;align-items:center;padding:60px 20px;gap:14px;text-align:center; }
.pc-empty-icon { font-size:36px;opacity:.3; }
.pc-fs { background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:12px;padding:22px;margin-bottom:18px; }
.pc-fs-title { font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:var(--text-color);opacity:1 !important;margin-bottom:18px;padding-bottom:11px;border-bottom:1px solid var(--body-color-less2);display:flex;align-items:center;justify-content:space-between; }
.pc-field { display:flex;flex-direction:column;gap:6px; }
.pc-label { font-size:12px;font-weight:600;color:var(--text-color);opacity:1 !important;display:flex;align-items:center;gap:6px; }
.pc-req { color:#ef4444; }
.pc-opt { font-size:10px;font-weight:500;background:rgba(100,116,139,.15);color:var(--text-color);opacity:.7;border-radius:4px;padding:1px 6px; }
.pc-input { width:100%;height:40px;background:var(--body-color);border:1.5px solid var(--body-color-less2);border-radius:8px;padding:0 14px;color:var(--text-color);font-family:inherit;font-size:14px;outline:none;transition:border-color .2s,box-shadow .2s;box-sizing:border-box; }
.pc-input::placeholder { color:var(--text-color);opacity:.45; }
.pc-input:focus { border-color:var(--btn-color);box-shadow:0 0 0 3px color-mix(in srgb,var(--btn-color) 12%,transparent); }
.pc-input.error { border-color:#ef4444; }
textarea.pc-input { height:90px;padding:11px 14px;resize:vertical;line-height:1.5; }
.pc-sel { width:100%;height:40px;padding:0 14px;background:var(--body-color);border:1.5px solid var(--body-color-less2);border-radius:8px;color:var(--text-color);font-family:inherit;font-size:14px;outline:none;appearance:auto;cursor:pointer;box-sizing:border-box;transition:border-color .2s; }
.pc-sel:focus { border-color:var(--btn-color);box-shadow:0 0 0 3px color-mix(in srgb,var(--btn-color) 12%,transparent); }
.pc-grid-2 { display:grid;grid-template-columns:1fr 1fr;gap:16px; }
.pc-grid-3 { display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px; }
.pc-grid-form { display:grid;gap:16px; }
.pc-radio-group { display:flex;gap:18px;flex-wrap:wrap;padding-top:8px; }
.pc-radio-opt { display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;color:var(--text-color); }
.pc-radio-opt input { accent-color:var(--btn-color);width:15px;height:15px; }
.pc-vo-block { background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:9px;padding:14px 16px;margin-bottom:12px; }
.pc-vo-header { display:flex;align-items:center;gap:10px;margin-bottom:10px; }
.pc-vo-values { display:flex;flex-wrap:wrap;gap:7px;margin-top:8px; }
.pc-vo-tag { display:flex;align-items:center;gap:5px;background:color-mix(in srgb,var(--btn-color) 12%,transparent);border:1px solid color-mix(in srgb,var(--btn-color) 25%,transparent);border-radius:6px;padding:4px 10px;font-size:12px;color:var(--btn-color); }
.pc-vo-tag-rm { background:none;border:none;color:var(--btn-color);cursor:pointer;font-size:13px;line-height:1;padding:0;opacity:.7;transition:opacity .15s; }
.pc-vo-tag-rm:hover { opacity:1;color:#ef4444; }
.pc-vo-add { display:flex;gap:8px;margin-top:10px; }
.pc-vo-add input { flex:1;height:32px;padding:0 10px;background:var(--body-color);border:1.5px solid var(--body-color-less2);border-radius:7px;color:var(--text-color);font-family:inherit;font-size:13px;outline:none;transition:border-color .2s; }
.pc-vo-add input:focus { border-color:var(--btn-color); }
.pc-vo-add input::placeholder { color:var(--text-color);opacity:.45; }
.pc-price-block { background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:10px;padding:18px;margin-bottom:14px;position:relative; }
.pc-pb-header { display:flex;align-items:center;justify-content:space-between;margin-bottom:14px; }
.pc-pb-type-toggle { display:flex;border:1px solid var(--body-color-less2);border-radius:7px;overflow:hidden; }
.pc-pb-type-btn {
  height:30px;
  padding:0 14px;
  background: #031260 !important;
  border:none;
  font-family:inherit;
  font-size:12px;
  font-weight:600;
  color: var(--btn-color);
  opacity: 0.7;
  cursor:pointer;
  transition:all .15s;
}

.pc-pb-type-btn.active {
  background: var(--btn-color) !important;
  color: #ffffff !important;
  opacity: 1 !important;
}
.pc-price-rm { position:absolute;top:14px;right:14px;width:26px;height:26px;border-radius:6px;background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.2);color:#f87171;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:13px;transition:all .15s; }
.pc-price-rm:hover { background:rgba(239,68,68,.15); }
.pc-sub-fields { margin-top:14px;padding-top:14px;border-top:1px solid var(--body-color-less2); }
.pc-attr-row { display:flex;gap:9px;align-items:center;margin-bottom:9px; }
.pc-attr-rm { width:26px;height:26px;border-radius:6px;background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.2);color:#f87171;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0;transition:all .15s; }
.pc-attr-rm:hover { background:rgba(239,68,68,.15); }
.pc-price-tbl { background:var(--body-color);border:1px solid var(--body-color-less2);border-radius:9px;overflow-x:auto;margin-top:4px;-webkit-overflow-scrolling:touch; }
.pc-price-tbl table { width:100%;border-collapse:collapse;min-width:480px; }
.pc-price-tbl thead th { font-size:11px;padding:9px 13px;background:color-mix(in srgb,var(--btn-color) 6%,transparent);color:var(--text-color);opacity:.8;text-transform:uppercase;letter-spacing:.6px;font-weight:700;text-align:left;white-space:nowrap; }
.pc-price-tbl tbody td { padding:10px 13px;font-size:12px;color:var(--text-color);border-top:1px solid var(--body-color-less2);white-space:nowrap; }
.pc-form-footer { display:flex;justify-content:flex-end;gap:10px;padding-top:18px;border-top:1px solid var(--body-color-less2);margin-top:4px; }
.pc-cat-grid { display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:18px;margin-top:20px; }
.pc-cat-card { background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:16px;overflow:hidden;transition:all .22s ease;cursor:pointer;display:flex;flex-direction:column; }
.pc-cat-card:hover { border-color:color-mix(in srgb,var(--btn-color) 50%,transparent);transform:translateY(-3px);box-shadow:0 12px 28px rgba(0,0,0,.14); }
.pc-cat-banner { height:88px;background:color-mix(in srgb,var(--btn-color) 12%,transparent);display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden; }
.pc-cat-banner-icon { font-size:36px;color:var(--btn-color);opacity:.5; }
.pc-cat-banner-ring { position:absolute;width:120px;height:120px;border-radius:50%;border:1.5px solid color-mix(in srgb,var(--btn-color) 18%,transparent);top:-30px;right:-30px; }
.pc-cat-banner-ring2 { position:absolute;width:70px;height:70px;border-radius:50%;border:1.5px solid color-mix(in srgb,var(--btn-color) 14%,transparent);bottom:-20px;left:20px; }
.pc-cat-body { padding:18px 18px 14px;display:flex;flex-direction:column;gap:6px;flex:1; }
.pc-cat-name { font-size:15px;font-weight:700;color:var(--text-color);margin:0;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
.pc-cat-desc { font-size:12px;color:var(--text-color);opacity:.55;line-height:1.5;margin:0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:36px; }
.pc-cat-meta { display:flex;align-items:center;gap:6px;margin-top:2px; }
.pc-cat-meta-pill { display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;padding:3px 8px;border-radius:20px;background:color-mix(in srgb,var(--btn-color) 10%,transparent);color:var(--btn-color); }
.pc-cat-divider { height:1px;background:var(--body-color-less2);margin:0 18px; }
.pc-cat-footer { padding:12px 18px;display:flex;align-items:center;justify-content:flex-end;gap:8px; }
.pc-cat-actions { display:flex;gap:8px; }
.pc-cat-btn { padding:6px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;transition:all .15s;border:none;background:transparent;font-family:inherit;display:inline-flex;align-items:center;gap:5px; }
.pc-cat-btn-edit { color:var(--btn-color);background:color-mix(in srgb,var(--btn-color) 10%,transparent); }
.pc-cat-btn-edit:hover { background:color-mix(in srgb,var(--btn-color) 20%,transparent); }
.pc-cat-btn-delete { color:#f87171;background:rgba(239,68,68,.08); }
.pc-cat-btn-delete:hover { background:rgba(239,68,68,.18); }
@media(max-width:500px) { .pc-cat-grid { grid-template-columns:1fr; } }
.pc-confirm-bg { position:fixed;inset:0;background:rgba(0,0,0,.65);backdrop-filter:blur(4px);z-index:1000;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;transition:opacity .2s; }
.pc-confirm-bg.open { opacity:1;pointer-events:all; }
.pc-confirm-box { background:var(--body-color-less);border:1px solid var(--body-color-less2);border-radius:14px;padding:24px;width:340px;max-width:90vw;transform:translateY(12px);transition:transform .2s; }
.pc-confirm-bg.open .pc-confirm-box { transform:translateY(0); }
.pc-toggle-row { display:flex;align-items:center;gap:10px;padding:10px 14px;background:var(--body-color);border:1.5px solid var(--body-color-less2);border-radius:8px;cursor:pointer;user-select:none;transition:border-color .15s; }
.pc-toggle-row.on { border-color:color-mix(in srgb,var(--btn-color) 40%,transparent); }
@keyframes pc-fade-up { from{opacity:0;transform:translateY(7px)} to{opacity:1;transform:translateY(0)} }
.pc-view-in { animation:pc-fade-up .2s ease; }
@media(max-width:700px) {
  .pc-grid-2,.pc-grid-3 { grid-template-columns:1fr; }
  .pc-topbar { flex-direction:column; }
  .pc-cat-grid { grid-template-columns:1fr; }
  .pc-page { padding:16px 10px 80px; }
  .pc-fs { padding:14px 10px; }
  .pc-fs-title { flex-wrap:wrap;gap:10px;font-size:11px; }
  .pc-price-block { padding:12px 10px; }
  .pc-pb-header { flex-wrap:wrap;gap:10px; }
  .pc-pb-type-toggle { flex-wrap:wrap; }
  .pc-pb-type-btn { padding:0 10px;font-size:11px; }
  .pc-form-footer { flex-wrap:wrap;justify-content:center; }
  .pc-attr-row { flex-wrap:wrap; }
  .pc-vo-add { flex-wrap:wrap; }
  .pc-vo-header { flex-wrap:wrap;gap:8px; }
  .pc-toggle-row { flex-wrap:wrap;gap:8px; }
  .pc-price-tbl { margin-left:-10px;margin-right:-10px;border-radius:0;border-left:none;border-right:none; }
}
`;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
function pricingSummary(prices) {
  if (!Array.isArray(prices) || !prices.length) return "—";
  const lines = prices.map((p) => {
    const currency =
      p.currency ||
      (p.currencies || []).find((c) => c.isDefault || c.default)?.currency ||
      p.currencies?.[0]?.currency ||
      "USD";
    const amount = parseFloat(
      p.amount ||
      (p.currencies || []).find((c) => c.isDefault || c.default)?.amount ||
      p.currencies?.[0]?.amount ||
      0
    ).toFixed(2);
    const amt = `${currency} ${amount}`;
    const isSub = p.priceType === "subscription" || p.priceType === "recurring";
    if (isSub)
      return `${amt} / ${(p.billingInterval || p.intervalType || "month").slice(
        0,
        2
      )}`;
    return `${amt} one-time`;
  });
  if (lines.length <= 2) return lines.join(", ");
  return lines.slice(0, 2).join(", ") + ` +${lines.length - 2} more`;
}

function StockBadge({ qty }) {
  if (qty === "" || qty === undefined || qty === null)
    return (
      <span
        className="pc-stock"
        style={{ background: "rgba(100,116,139,.15)", color: "#94a3b8" }}
      >
        Untracked
      </span>
    );
  const n = parseInt(qty);
  if (n === 0)
    return (
      <span
        className="pc-stock"
        style={{ background: "rgba(239,68,68,.12)", color: "#f87171" }}
      >
        Out of stock
      </span>
    );
  if (n <= 10)
    return (
      <span
        className="pc-stock"
        style={{ background: "rgba(245,158,11,.15)", color: "#fbbf24" }}
      >
        Low — {n}
      </span>
    );
  return (
    <span
      className="pc-stock"
      style={{ background: "rgba(16,185,129,.12)", color: "#34d399" }}
    >
      In stock — {n}
    </span>
  );
}

const sanitizeAmountInput = (raw) => {
  let v = String(raw).replace(/[^0-9.]/g, "");
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  const parts = v.split(".");
  const trimmedInt = (parts[0] || "").slice(0, 13);
  if (parts.length > 1) return trimmedInt + "." + parts[1].slice(0, 2);
  return trimmedInt;
};

function Field({ label, req, opt, error, children, full }) {
  return (
    <div className="pc-field" style={full ? { gridColumn: "1/-1" } : {}}>
      <label className="pc-label">
        {label}
        {req && <span className="pc-req"> *</span>}
        {opt && <span className="pc-opt">{opt}</span>}
      </label>
      {children}
      {error && (
        <span style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>
          {error}
        </span>
      )}
    </div>
  );
}

function Toggle({ on, onToggle, label, sublabel }) {
  return (
    <div className={`pc-toggle-row${on ? " on" : ""}`} onClick={onToggle}>
      <div
        style={{
          width: 36,
          height: 20,
          borderRadius: 10,
          background: on ? "var(--btn-color)" : "var(--body-color-less2)",
          position: "relative",
          transition: "background .2s",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            position: "absolute",
            top: 3,
            left: on ? 19 : 3,
            transition: "left .2s",
            boxShadow: "0 1px 3px rgba(0,0,0,.25)",
          }}
        />
      </div>
      <div>
        <div
          style={{ fontSize: 13, fontWeight: 600, color: "var(--text-color)" }}
        >
          {label}
        </div>
        {sublabel && (
          <div
            style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.6 }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}

function BackLink({ onClick, label }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "16px",
        cursor: "pointer",
        width: "fit-content",
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ color: "var(--btn-color)" }}
      >
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
      </svg>
      <span style={{
        fontSize: "14px",
        fontWeight: "500",
        color: "var(--btn-color)",
        fontFamily: "inherit"
      }}>
        {label}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// VOBlock
// ─────────────────────────────────────────────────────────────────
function VOBlock({ vo, onChange, onRemove, nameError }) {
  const [newVal, setNewVal] = React.useState("");
  const [valError, setValError] = React.useState("");
  const add = () => {
    const v = newVal.trim();
    if (!v) return;
    if ((vo.values || []).includes(v)) { setValError("Value already exists"); return; }
    if (v.length > 100) { setValError("Value must be at most 100 characters"); return; }
    setValError("");
    onChange({ ...vo, values: [...(vo.values || []), v] });
    setNewVal("");
  };
  return (
    <div className="pc-vo-block">
      <div className="pc-vo-header">
        <input
          className="pc-input"
          placeholder="Attribute name (e.g. size, color)"
          value={vo.name}
          onChange={(e) => {
            const v = e.target.value;
            if (/^[a-zA-Z0-9\s\+\-\&\_\.\/]*$/.test(v) && v.length <= 50) onChange({ ...vo, name: v });
          }}
          style={{ height: 34, fontSize: 13, flex: 1, borderColor: nameError ? "#ef4444" : undefined }}
        />
        <button
          className="pc-btn-danger"
          style={{
            width: 30,
            height: 30,
            padding: 0,
            justifyContent: "center",
            borderRadius: 7,
            flexShrink: 0,
          }}
          onClick={onRemove}
        >
          ×
        </button>
      </div>
      {nameError && <span style={{ fontSize: 11, color: "#ef4444", display: "block", marginTop: 4, marginBottom: 4 }}>{nameError}</span>}
      <div
        style={{
          fontSize: 11,
          color: "var(--text-color)",
          opacity: 0.6,
          marginBottom: 8,
        }}
      >
        Add values below — these become the selectable options at checkout
      </div>
      <div className="pc-vo-values">
        {(vo.values || []).map((v, i) => (
          <span key={i} className="pc-vo-tag">
            {v}
            <button
              className="pc-vo-tag-rm"
              onClick={() =>
                onChange({ ...vo, values: vo.values.filter((_, j) => j !== i) })
              }
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="pc-vo-add">
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          <input
            placeholder="Add value then press Enter (e.g. S, M, L)"
            value={newVal}
            onChange={(e) => { setNewVal(e.target.value); setValError(""); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); add(); }
            }}
            style={{ borderColor: valError ? "#ef4444" : undefined }}
          />
          {valError && <span style={{ fontSize: 11, color: "#ef4444" }}>{valError}</span>}
        </div>
        <button className="pc-btn pc-btn-secondary pc-btn-sm" onClick={add}>
          Add
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PriceBlock
// ─────────────────────────────────────────────────────────────────
function PriceBlock({
  pb,
  onUpdate,
  onRemove,
  isOnly,
  variants,
  onSetDefault,
  pErrors = {},
}) {
  const isSub = pb.priceType === "subscription";
  const isEditMode = !!pb.id && typeof pb.id === 'string' && pb.id.startsWith('price_');
  const [originalCurrencyIds] = React.useState(() => {
    if (isEditMode && isSub) {
      return new Set((pb.extraCurrencies || []).map(ec => ec.id));
    }
    return new Set();
  });

  const extra = pb.extraCurrencies || [];
  const addCur = () => {
    const usedCodes = new Set([
      pb.currency || "USD",
      ...extra.map((ec) => ec.currency),
    ]);
    const firstAvailable = CURRENCIES.find((c) => !usedCodes.has(c.code));
    if (!firstAvailable) return;
    onUpdate({
      ...pb,
      extraCurrencies: [
        ...extra,
        { id: `new_${Date.now()}`, currency: firstAvailable.code, amount: "" },
      ],
    });
  };
  const updCur = (i, u) =>
    onUpdate({
      ...pb,
      extraCurrencies: extra.map((x, j) => (j === i ? { ...x, ...u } : x)),
    });
  const rmCur = (i) =>
    onUpdate({ ...pb, extraCurrencies: extra.filter((_, j) => j !== i) });
  const hasVariants =
    variants &&
    variants.length > 0 &&
    variants.some((v) => v.name && v.values?.length > 0);
  const variantObj = pb.variantObj || {};
  const isLockedSubscription = isEditMode && isSub;
  const isOriginalCurrency = (ec) => {
    if (!isLockedSubscription) return false;
    return originalCurrencyIds.has(ec.id);
  };

  // Check if any extra currency is set as default
  const hasExtraDefault = (pb.extraCurrencies || []).some(ec => ec.isDefault);

  return (
    <div className="pc-price-block">
      <div className="pc-pb-header">
        <div className="pc-pb-type-toggle">
          {[
            { value: "onetime", label: "One-time" },
            { value: "subscription", label: "Subscription" }
          ].map((t) => {
            const isActive = pb.priceType === t.value ||
              (t.value === "subscription" && pb.priceType === "recurring");

            return (
              <button
                key={t.value}
                className={`pc-pb-type-btn${isActive ? " active" : ""}`}
                onClick={() => {
                  if (isLockedSubscription && t.value !== "subscription") {
                    showToast("error", "Cannot change price type for existing subscription. Please create a new price instead.");
                    return;
                  }
                  onUpdate({ ...pb, priceType: t.value });
                }}
                style={{
                  opacity: isLockedSubscription && t.value !== "subscription" ? 0.5 : 1,
                  cursor: isLockedSubscription && t.value !== "subscription" ? "not-allowed" : "pointer",
                }}
                disabled={isLockedSubscription && t.value !== "subscription"}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {pb.isDefault ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "0 10px",
                height: 28,
                borderRadius: 8,
                background: "color-mix(in srgb,var(--btn-color) 15%,transparent)",
                border: "1.5px solid color-mix(in srgb,var(--btn-color) 35%,transparent)",
                color: "var(--btn-color)",
                fontSize: 11,
                fontWeight: 700,
                flexShrink: 0,
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              <i className="mdi mdi-star" style={{ fontSize: 12 }} /> Default
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onSetDefault && onSetDefault(pb.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "0 10px",
                height: 28,
                borderRadius: 8,
                background: "transparent",
                border: "1.5px dashed color-mix(in srgb,var(--btn-color) 40%,transparent)",
                color: "var(--btn-color)",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                flexShrink: 0,
                whiteSpace: "nowrap",
                opacity: 0.75,
                transition: "all .15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 10%,transparent)";
                e.currentTarget.style.borderStyle = "solid";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.75";
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderStyle = "dashed";
              }}
            >
              <i className="mdi mdi-star-outline" style={{ fontSize: 12 }} /> Set Default
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "rgba(239,68,68,.1)",
              border: "1.5px solid rgba(239,68,68,.3)",
              color: "#f87171",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              flexShrink: 0,
            }}
            title="Delete this price"
          >
            ×
          </button>
        </div>
      </div>

      {isLockedSubscription && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            borderRadius: 8,
            background: "rgba(245,158,11,.12)",
            border: "1px solid rgba(245,158,11,.25)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#f59e0b",
          }}
        >
          <i className="mdi mdi-lock-outline" style={{ fontSize: 14 }} />
          <span>
            <strong>Subscription price is locked.</strong> Only additional currencies can be added. Existing currency entries cannot be modified or deleted.
          </span>
        </div>
      )}

      <div className="pc-grid-2" style={{ marginBottom: 14 }}>
        <Field label="Currency">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              className="pc-sel"
              value={pb.currency || "USD"}
              onChange={(e) => {
                if (isLockedSubscription) {
                  showToast("error", "Cannot modify currency for existing subscription price.");
                  return;
                }
                onUpdate({
                  ...pb,
                  currency: e.target.value,
                  extraCurrencies: extra.filter(
                    (ec) => ec.currency !== e.target.value
                  ),
                });
              }}
              disabled={isLockedSubscription}
              style={{
                opacity: isLockedSubscription ? 0.6 : 1,
                cursor: isLockedSubscription ? "not-allowed" : "pointer",
                flex: 1,
              }}
            >
              {CURRENCIES.filter(
                (c) => !extra.some((ec) => ec.currency === c.code)
              ).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.code} — {c.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const extraCurrencies = pb.extraCurrencies || [];
                const totalCurrencies = 1 + extraCurrencies.length;
                if (totalCurrencies <= 1) {
                  showToast("error", "At least one primary currency is required");
                  return;
                }
                if (isLockedSubscription) return;
                const newPrimaryCurrency = extraCurrencies.length > 0 ? extraCurrencies[0].currency : null;
                onUpdate({
                  ...pb,
                  currency: newPrimaryCurrency,
                  extraCurrencies: extraCurrencies.length > 0 ? extraCurrencies.slice(1) : [],
                });
              }}
              disabled={isLockedSubscription || (1 + (pb.extraCurrencies || []).length) <= 1}
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "rgba(239,68,68,.1)",
                border: "1px solid rgba(239,68,68,.3)",
                color: "#ef4444",
                fontSize: 16,
                cursor: isLockedSubscription || (1 + extra.length) <= 1 ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: isLockedSubscription || (1 + extra.length) <= 1 ? 0.5 : 1,
                flexShrink: 0,
              }}
              title={(1 + extra.length) <= 1 ? "At least one currency required" : "Remove currency"}
            >
              ×
            </button>
          </div>
        </Field>
        <Field label="Amount" error={pErrors.amount}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              className={`pc-input${pErrors.amount ? " error" : ""}`}
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={pb.amount}
              onChange={(e) => {
                if (isLockedSubscription) {
                  showToast("error", "Cannot modify amount for existing subscription price.");
                  return;
                }
                onUpdate({ ...pb, amount: sanitizeAmountInput(e.target.value) });
              }}
              disabled={isLockedSubscription}
              style={{
                opacity: isLockedSubscription ? 0.6 : 1,
                cursor: isLockedSubscription ? "not-allowed" : "text",
                flex: 1,
              }}
            />
            {hasExtraDefault ? (
              <button
                type="button"
                onClick={() => {
                  if (isLockedSubscription) return;
                  onUpdate({
                    ...pb,
                    extraCurrencies: (pb.extraCurrencies || []).map((ec) => ({ ...ec, isDefault: false })),
                  });
                }}
                title="Extra currency is set as default. Click to make this the default."
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "0 10px",
                  height: 40,
                  borderRadius: 8,
                  background: "transparent",
                  border: "1.5px dashed color-mix(in srgb,var(--btn-color) 40%,transparent)",
                  color: "var(--btn-color)",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: isLockedSubscription ? "not-allowed" : "pointer",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  opacity: isLockedSubscription ? 0.5 : 0.75,
                  transition: "all .15s",
                }}
                onMouseEnter={(e) => {
                  if (isLockedSubscription) return;
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 10%,transparent)";
                  e.currentTarget.style.borderStyle = "solid";
                }}
                onMouseLeave={(e) => {
                  if (isLockedSubscription) return;
                  e.currentTarget.style.opacity = "0.75";
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderStyle = "dashed";
                }}
              >
                <i className="mdi mdi-star-outline" style={{ fontSize: 12 }} /> Set as Default
              </button>
            ) : (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "0 10px",
                  height: 40,
                  borderRadius: 8,
                  background: "color-mix(in srgb,var(--btn-color) 15%,transparent)",
                  border: "1.5px solid color-mix(in srgb,var(--btn-color) 35%,transparent)",
                  color: "var(--btn-color)",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                <i className="mdi mdi-star" style={{ fontSize: 12 }} /> Default
              </div>
            )}
          </div>
        </Field>
      </div>

      {/* Rest of the component remains the same */}
      <div style={{ marginBottom: 14 }}>
        <Field label="SKU" opt="optional" error={pErrors.sku}>
          <input
            className={`pc-input${pErrors.sku ? " error" : ""}`}
            placeholder="e.g. TSHIRT-M-RED"
            value={pb.sku || ""}
            onChange={(e) => {
              if (isLockedSubscription) {
                showToast("error", "Cannot modify SKU for existing subscription price.");
                return;
              }
              const v = e.target.value;
              if (/^[A-Za-z0-9\-_]*$/.test(v) && v.length <= 50) onUpdate({ ...pb, sku: v });
            }}
            disabled={isLockedSubscription}
            style={{
              opacity: isLockedSubscription ? 0.6 : 1,
              cursor: isLockedSubscription ? "not-allowed" : "text",
            }}
          />
        </Field>
      </div>

      {hasVariants && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--text-color)",
              opacity: 0.8,
              marginBottom: 10,
            }}
          >
            Variant for this price
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {variants
              .filter((v) => v.name && v.values?.length > 0)
              .map((v) => (
                <Field key={v.id} label={v.name}>
                  <select
                    className="pc-sel"
                    value={variantObj[v.name] || ""}
                    onChange={(e) => {
                      if (isLockedSubscription) {
                        showToast("error", "Cannot modify variant for existing subscription price.");
                        return;
                      }
                      onUpdate({
                        ...pb,
                        variantObj: { ...variantObj, [v.name]: e.target.value },
                      });
                    }}
                    disabled={isLockedSubscription}
                    style={{
                      width: "auto",
                      minWidth: 120,
                      opacity: isLockedSubscription ? 0.6 : 1,
                      cursor: isLockedSubscription ? "not-allowed" : "pointer",
                    }}
                  >
                    <option value="">— Any —</option>
                    {v.values.map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </Field>
              ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <Toggle
          on={!!pb.trackInventory}
          onToggle={() => {
            if (isLockedSubscription) {
              showToast("error", "Cannot modify inventory tracking for existing subscription price.");
              return;
            }
            onUpdate({ ...pb, trackInventory: !pb.trackInventory });
          }}
          label="Track Inventory"
          sublabel="Enable to set a stock quantity for this price"
        />
        {pb.trackInventory && (
          <div style={{ marginTop: 10 }}>
            <Field label="Stock Quantity" error={pErrors.qty}>
              <input
                className={`pc-input${pErrors.qty ? " error" : ""}`}
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={pb.inventoryQty || ""}
                onChange={(e) => {
                  if (isLockedSubscription) {
                    showToast("error", "Cannot modify inventory quantity for existing subscription price.");
                    return;
                  }
                  const v = e.target.value;
                  if (/^\d{0,5}$/.test(v)) onUpdate({ ...pb, inventoryQty: v });
                }}
                style={{ width: 180, opacity: isLockedSubscription ? 0.6 : 1 }}
                disabled={isLockedSubscription}
              />
            </Field>
          </div>
        )}
      </div>

      {isSub && (
        <div className="pc-sub-fields">
          <div className="pc-grid-2" style={{ marginBottom: 14 }}>
            <Field label="Billing Interval">
              <select
                className="pc-sel"
                value={pb.billingInterval || "Month"}
                onChange={(e) => {
                  if (isLockedSubscription) {
                    showToast("error", "Cannot modify billing interval for existing subscription price.");
                    return;
                  }
                  onUpdate({ ...pb, billingInterval: e.target.value });
                }}
                disabled={isLockedSubscription}
                style={{
                  opacity: isLockedSubscription ? 0.6 : 1,
                  cursor: isLockedSubscription ? "not-allowed" : "pointer",
                }}
              >
                {["Week", "Month", "Year"].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Every" error={pErrors.billingEvery}>
              <input
                className={`pc-input${pErrors.billingEvery ? " error" : ""}`}
                type="text"
                inputMode="numeric"
                placeholder="1"
                value={pb.billingEvery ?? ""}
                onChange={(e) => {
                  if (isLockedSubscription) {
                    showToast("error", "Cannot modify billing frequency for existing subscription price.");
                    return;
                  }
                  const v = e.target.value;
                  if (/^\d{0,3}$/.test(v)) onUpdate({ ...pb, billingEvery: v });
                }}
                disabled={isLockedSubscription}
                style={{
                  opacity: isLockedSubscription ? 0.6 : 1,
                  cursor: isLockedSubscription ? "not-allowed" : "text",
                }}
              />
            </Field>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Field label="Trial Period" opt="optional">
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  className={`pc-input${pErrors.trialDays ? " error" : ""}`}
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 14"
                  value={pb.trialDays || ""}
                  onChange={(e) => {
                    if (isLockedSubscription) {
                      showToast("error", "Cannot modify trial period for existing subscription price.");
                      return;
                    }
                    const v = e.target.value;
                    if (/^\d{0,3}$/.test(v)) onUpdate({ ...pb, trialDays: v });
                  }}
                  style={{ width: 120, opacity: isLockedSubscription ? 0.6 : 1 }}
                  disabled={isLockedSubscription}
                />
                {pErrors.trialDays && <span style={{ fontSize: 11, color: "#ef4444" }}>{pErrors.trialDays}</span>}
                <span style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.6 }}>days</span>
                {pb.trialDays && parseInt(pb.trialDays) > 0 && (
                  <span style={{ fontSize: 11, background: "rgba(16,185,129,.15)", color: "#10b981", padding: "2px 8px", borderRadius: "12px", fontWeight: 600 }}>
                    ✓ Trial active
                  </span>
                )}
              </div>
            </Field>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="pc-label" style={{ marginBottom: 8 }}>Billing Duration</label>
            <div style={{ display: "flex", gap: 24 }}>
              {["no-end", "fixed"].map((opt) => (
                <label key={opt} className="pc-radio-opt" style={{
                  opacity: isLockedSubscription ? 0.6 : 1,
                  cursor: isLockedSubscription ? "not-allowed" : "pointer",
                }}>
                  <input
                    type="radio"
                    name={`dur-${pb.id}`}
                    value={opt}
                    checked={(pb.billingDuration || "no-end") === opt}
                    onChange={() => {
                      if (isLockedSubscription) {
                        showToast("error", "Cannot modify billing duration for existing subscription price.");
                        return;
                      }
                      onUpdate({ ...pb, billingDuration: opt });
                    }}
                    disabled={isLockedSubscription}
                  />
                  {opt === "no-end" ? "No end date" : "Fixed cycles"}
                </label>
              ))}
            </div>
            {(pb.billingDuration || "no-end") === "fixed" && (
              <div style={{ marginTop: 10 }}>
                <Field label="Total Cycles">
                  <input
                    className={`pc-input${pErrors.totalCycles ? " error" : ""}`}
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 12"
                    value={pb.totalCycles}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || (/^\d+$/.test(v) && parseInt(v) <= 10000)) onUpdate({ ...pb, totalCycles: v });
                    }}
                    style={{ width: 180 }}
                  />
                  {pErrors.totalCycles && <span style={{ fontSize: 11, color: "#ef4444", display: "block", marginTop: 4 }}>{pErrors.totalCycles}</span>}
                </Field>
              </div>
            )}
          </div>

          <div className="pc-grid-2" style={{ marginBottom: 14 }}>
            <Field label="Retry Attempts" error={pErrors.retryAttempts}>
              <input
                className={`pc-input${pErrors.retryAttempts ? " error" : ""}`}
                type="text"
                inputMode="numeric"
                placeholder="3"
                value={pb.retryAttempts !== undefined && pb.retryAttempts !== null ? pb.retryAttempts : "3"}
                onChange={(e) => {
                  if (isLockedSubscription) {
                    showToast("error", "Cannot modify retry attempts for existing subscription price.");
                    return;
                  }
                  const v = e.target.value;
                  if (/^\d{0,3}$/.test(v) && (v === "" || parseInt(v) <= 100)) onUpdate({ ...pb, retryAttempts: v });
                }}
                disabled={isLockedSubscription}
                style={{
                  opacity: isLockedSubscription ? 0.6 : 1,
                  cursor: isLockedSubscription ? "not-allowed" : "text",
                }}
              />
            </Field>
            <Field label="Retry Interval">
              <select
                className="pc-sel"
                value={pb.retryInterval || "Day"}
                onChange={(e) => {
                  if (isLockedSubscription) {
                    showToast("error", "Cannot modify retry interval for existing subscription price.");
                    return;
                  }
                  onUpdate({ ...pb, retryInterval: e.target.value });
                }}
                disabled={isLockedSubscription}
                style={{
                  opacity: isLockedSubscription ? 0.6 : 1,
                  cursor: isLockedSubscription ? "not-allowed" : "pointer",
                }}
              >
                {["Day", "Week", "Month"].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: "1px solid var(--body-color-less2)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--text-color)",
            opacity: 0.6,
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          Additional Currencies
          {isLockedSubscription && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                background: "rgba(16,185,129,.15)",
                color: "#10b981",
                padding: "2px 8px",
                borderRadius: 12,
              }}
            >
              Add Only
            </span>
          )}
        </div>
        {extra.map((ec, i) => {
          const isOriginal = isOriginalCurrency(ec);
          const canEdit = !isLockedSubscription || !isOriginal;
          const canDelete = !isLockedSubscription || !isOriginal;
          const isDefaultCur = !!ec.isDefault;

          return (
            <div
              key={ec.id}
              className="pc-extra-cur-row"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 9,
                alignItems: "flex-end",
                marginBottom: 10,
                opacity: isLockedSubscription && isOriginal ? 0.7 : 1,
              }}
            >
              <div style={{ flex: "1 1 120px", minWidth: 0 }}>
                <Field label={i === 0 ? "Currency" : ""}>
                  <select
                    className="pc-sel"
                    value={ec.currency}
                    onChange={(e) => canEdit && updCur(i, { currency: e.target.value })}
                    disabled={!canEdit}
                    style={{
                      opacity: !canEdit ? 0.6 : 1,
                      cursor: !canEdit ? "not-allowed" : "pointer",
                    }}
                  >
                    {CURRENCIES.filter(
                      (c) =>
                        c.code !== (pb.currency || "USD") &&
                        (c.code === ec.currency ||
                          !extra.some((x, j) => j !== i && x.currency === c.code))
                    ).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div style={{ flex: "1 1 80px", minWidth: 0 }}>
                <Field label={i === 0 ? "Amount" : ""} error={pErrors.extraAmounts && pErrors.extraAmounts[i]}>
                  <input
                    className={`pc-input${pErrors.extraAmounts && pErrors.extraAmounts[i] ? " error" : ""}`}
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={ec.amount}
                    onChange={(e) => canEdit && updCur(i, { amount: sanitizeAmountInput(e.target.value) })}
                    disabled={!canEdit}
                    style={{
                      opacity: !canEdit ? 0.6 : 1,
                      cursor: !canEdit ? "not-allowed" : "text",
                    }}
                  />
                </Field>
              </div>

              <div style={{ display: "flex", gap: 7, alignItems: "center", flexShrink: 0 }}>
              {isDefaultCur ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "0 10px",
                    height: 28,
                    borderRadius: 8,
                    background: "color-mix(in srgb,var(--btn-color) 15%,transparent)",
                    border: "1.5px solid color-mix(in srgb,var(--btn-color) 35%,transparent)",
                    color: "var(--btn-color)",
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    userSelect: "none",
                  }}
                >
                  <i className="mdi mdi-star" style={{ fontSize: 12 }} /> Default
                </div>
              ) : (
                <button
                  title={isLockedSubscription ? "Cannot change default for existing subscription" : "Set as default currency"}
                  disabled={isLockedSubscription}
                  onClick={() => {
                    if (isLockedSubscription) return;
                    onUpdate({
                      ...pb,
                      extraCurrencies: extra.map((x, j) => ({
                        ...x,
                        isDefault: j === i,
                      })),
                    });
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "0 10px",
                    height: 28,
                    borderRadius: 8,
                    background: "transparent",
                    border: `1.5px dashed ${isLockedSubscription ? "rgba(100,116,139,.3)" : "color-mix(in srgb,var(--btn-color) 40%,transparent)"}`,
                    color: isLockedSubscription ? "var(--text-color)" : "var(--btn-color)",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: isLockedSubscription ? "not-allowed" : "pointer",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    opacity: isLockedSubscription ? 0.35 : 0.75,
                    transition: "all .15s",
                  }}
                  onMouseEnter={(e) => {
                    if (isLockedSubscription) return;
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 10%,transparent)";
                    e.currentTarget.style.borderStyle = "solid";
                  }}
                  onMouseLeave={(e) => {
                    if (isLockedSubscription) return;
                    e.currentTarget.style.opacity = "0.75";
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderStyle = "dashed";
                  }}
                >
                  <i className="mdi mdi-star-outline" style={{ fontSize: 12 }} /> Set Default
                </button>
              )}

              {canDelete ? (
                <button
                  onClick={() => rmCur(i)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "rgba(239,68,68,.1)",
                    border: "1.5px solid rgba(239,68,68,.3)",
                    color: "#f87171",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 15,
                    flexShrink: 0,
                  }}
                >
                  ×
                </button>
              ) : (
                <div style={{ width: 28, flexShrink: 0 }} />
              )}
              </div>
            </div>
          );
        })}
        {CURRENCIES.length > extra.length + 1 && (
          <button
            onClick={addCur}
            style={{
              background: "transparent",
              border: "1.5px dashed color-mix(in srgb,var(--btn-color) 45%,transparent)",
              color: "var(--btn-color)",
              fontFamily: "inherit",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 8,
              opacity: 0.85,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add another currency
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ProductForm
// ─────────────────────────────────────────────────────────────────
function ProductForm({ initial, onSave, onCancel, saving }) {
  const isEdit = !!initial?.id;
  const [name, setName] = React.useState(initial?.name || "");
  const [status, setStatus] = React.useState(initial?.status || "Active");
  const [desc, setDesc] = React.useState(initial?.description || "");
  const [imageUrl, setImageUrl] = React.useState(initial?.imageS3Key ? "" : (initial?.imageUrl || ""));
  const [imageMode, setImageMode] = React.useState(initial?.imageS3Key ? "upload" : "url");
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = React.useState(null);
  const [imageError, setImageError] = React.useState("");

  React.useEffect(() => {
    if (!imageFile) { setImagePreviewUrl(null); return; }
    const url = URL.createObjectURL(imageFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);
  const [imageS3Url, setImageS3Url] = React.useState(initial?.imageS3Key ? (initial?.imageUrl || "") : "");
  const [variants, setVariants] = React.useState(initial?.variants || []);
  const [prices, setPrices] = React.useState(
    initial?.prices?.length
      ? initial.prices.map((p) => ({
        ...p,
        trialDays: p.trialDays !== undefined && p.trialDays !== null ? String(p.trialDays) : p.priceType === "subscription" ? "0" : "",
        priceType: p.priceType === "recurring" ? "subscription" : p.priceType,
        billingEvery: p.billingEvery || "1",
        retryAttempts: p.retryAttempts || "3",
        retryInterval: p.retryInterval || "Day",
        totalCycles: p.totalCycles ? String(p.totalCycles) : "0",
        billingDuration: p.totalCycles ? "fixed" : (p.billingDuration || "no-end"),
      }))
      : [
        {
          id: Date.now(),
          priceType: "onetime",
          amount: "",
          currency: "USD",
          sku: "",
          isDefault: true,
          trackInventory: false,
          inventoryQty: "",
          variantObj: {},
          billingInterval: "Month",
          billingEvery: "1",
          billingDuration: "no-end",
          totalCycles: "0",
          retryAttempts: "3",
          retryInterval: "Day",
          trialDays: "",
          extraCurrencies: [],
        },
      ]
  );
  const [attrs, setAttrs] = React.useState(initial?.attrs || []);
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    if (prices.length && prices.every((p) => p.amount && String(p.amount).trim() !== "")) {
      setErrors((ex) => ({ ...ex, prices: null }));
    }
  }, [prices]);

  const originalPricesRef = React.useRef(
    initial?.prices?.map((p) => ({
      id: p.id,
      currency: p.currency,
      extraCurrencies: (p.extraCurrencies || []).map((ec) => ec.currency),
    })) || []
  );

  const validate = () => {
    const e = {};

    // Product Name
    if (!name.trim()) e.name = "Product name is required";
    else if (name.trim().length < 2) e.name = "Product name must be at least 2 characters";
    else if (name.trim().length > 100) e.name = "Product name must be at most 100 characters";
    else if (!/^[a-zA-Z0-9\s\-&]+$/.test(name.trim())) e.name = "Only letters, numbers, spaces, - and & are allowed";

    if (status === "Draft") {
      setErrors(e);
      return !Object.keys(e).length;
    }

    // Description (optional but if provided must be 10–1000)
    if (desc.trim() && desc.trim().length < 10) e.desc = "Description must be at least 10 characters";
    else if (desc.trim().length > 1000) e.desc = "Description must be at most 1000 characters";

    // Prices: required amount
    if (!prices.length) e.prices = "At least one price entry is required";
    prices.forEach((p, i) => {
      const amtStr = String(p.amount || "").trim();
      if (!amtStr) {
        e[`price_${i}_amount`] = "Amount is required";
      } else if (!/^\d+(\.\d+)?$/.test(amtStr)) {
        e[`price_${i}_amount`] = "Amount must be numeric";
      } else {
        const [intPart, decPart] = amtStr.split(".");
        const totalDigits = intPart.replace(/^0+/, "").length + (decPart ? decPart.length : 0);
        if (totalDigits > 15) e[`price_${i}_amount`] = "Amount must not exceed 15 digits total";
        else if (decPart && decPart.length > 2) e[`price_${i}_amount`] = "Amount allows up to 2 decimal places";
        else if (parseFloat(amtStr) < 0.10) e[`price_${i}_amount`] = "Amount must be at least 0.10";
      }
      // Extra currency amounts
      (p.extraCurrencies || []).forEach((ec, j) => {
        const ecStr = String(ec.amount || "").trim();
        if (!ecStr) {
          e[`price_${i}_extraAmount_${j}`] = "Amount is required";
        } else if (!/^\d+(\.\d+)?$/.test(ecStr)) {
          e[`price_${i}_extraAmount_${j}`] = "Amount must be numeric";
        } else {
          const [intPart, decPart] = ecStr.split(".");
          const totalDigits = intPart.replace(/^0+/, "").length + (decPart ? decPart.length : 0);
          if (totalDigits > 15) e[`price_${i}_extraAmount_${j}`] = "Amount must not exceed 15 digits total";
          else if (decPart && decPart.length > 2) e[`price_${i}_extraAmount_${j}`] = "Amount allows up to 2 decimal places";
          else if (parseFloat(ecStr) < 0.10) e[`price_${i}_extraAmount_${j}`] = "Amount must be at least 0.10";
        }
      });
    });

    // Per-price validation
    prices.forEach((p, i) => {
      if (p.sku) {
        if (p.sku.length > 50) e[`price_${i}_sku`] = "SKU max 50 characters";
        else if (!/^[A-Za-z0-9\-_]+$/.test(p.sku)) e[`price_${i}_sku`] = "SKU: only A-Z, 0-9, - and _ allowed, no spaces";
        else if (prices.some((other, j) => j !== i && other.sku && other.sku === p.sku)) e[`price_${i}_sku`] = "SKU must be unique across prices";
      }
      if (p.trackInventory && p.inventoryQty !== "" && p.inventoryQty !== undefined) {
        const qty = String(p.inventoryQty);
        if (qty.includes('.') || isNaN(parseInt(qty)) || parseInt(qty) < 0 || parseInt(qty) > 99999) e[`price_${i}_qty`] = "Stock quantity: whole number 0–99999";
      }
      if (p.priceType === "subscription") {
        const every = String(p.billingEvery || "");
        if (!every || every.includes('.') || parseInt(every) < 1 || every.length > 3) e[`price_${i}_billingEvery`] = "Must be a whole number ≥ 1 (max 3 digits)";
        if (p.trialDays && p.trialDays !== "0") {
          const td = String(p.trialDays);
          if (td.includes('.') || parseInt(td) < 0 || td.replace(/^-/, "").length > 3) e[`price_${i}_trialDays`] = "Trial period: whole number, max 3 digits";
        }
        const retry = String(p.retryAttempts !== undefined ? p.retryAttempts : "3");
        if (retry.includes('.') || isNaN(parseInt(retry)) || parseInt(retry) < 0 || parseInt(retry) > 100) e[`price_${i}_retryAttempts`] = "Retry attempts: whole number 0–100";
        if ((p.billingDuration || "no-end") === "fixed") {
          const cycles = String(p.totalCycles || "");
          if (!cycles || cycles.includes('.') || parseInt(cycles) < 1 || parseInt(cycles) > 10000) e[`price_${i}_totalCycles`] = "Total cycles: whole number 1–10000";
        }
      }
    });

    // Attribute names
    variants.forEach((v, i) => {
      if (v.name && v.name.length < 2) e[`variantName_${i}`] = "Attribute name must be at least 2 characters";
      else if (v.name && v.name.length > 50) e[`variantName_${i}`] = "Attribute name must be at most 50 characters";
      else if (v.name && !/^[a-zA-Z0-9\s\+\-\&\_\.\/]+$/.test(v.name)) e[`variantName_${i}`] = "Attribute name: only letters, numbers, spaces and basic symbols allowed";
    });

    // Metadata keys
    attrs.forEach((a, i) => {
      if (a.key && a.key.length > 50) e[`attrKey_${i}`] = "Key must be at most 50 characters";
      else if (a.key && !/^[a-zA-Z0-9_\+\-]+$/.test(a.key)) e[`attrKey_${i}`] = "Key: only letters, numbers, _, + and - allowed";
    });

    setErrors(e);
    return !Object.keys(e).length;
  };

  const priceSummaryRows = prices
    .filter((p) => p.amount)
    .map((p) => ({
      variant: Object.entries(p.variantObj || {}).map(([k, v]) => `${k}: ${v}`).join(" / ") || "—",
      amount: `${p.currency || "USD"} ${parseFloat(p.amount || 0).toFixed(2)}`,
      type: p.priceType === "subscription" || p.priceType === "recurring" ? `${p.billingInterval || "Month"} subscription` : "One-time",
      sku: p.sku || "—",
      inventory: p.trackInventory ? `${p.inventoryQty || 0} units` : "Untracked",
    }));

  return (
    <div className="pc-view-in">
      <BackLink onClick={onCancel} label="Back to Products" />
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-color)", marginBottom: 20, letterSpacing: "-.3px" }}>
        {isEdit ? `Edit — ${initial.name}` : "Add New Product"}
      </div>

      <div className="pc-fs">
        <div className="pc-fs-title">Basic Info</div>
        <div className="pc-grid-form" style={{ gap: 16 }}>
          <Field label="Product Name" req error={errors.name} full>
            <input className={`pc-input${errors.name ? " error" : ""}`} placeholder="e.g. Classic T-Shirt" value={name} onChange={(e) => { const v = e.target.value; if (/^[a-zA-Z0-9\s\-&]*$/.test(v) && v.length <= 100) { setName(v); setErrors((ex) => ({ ...ex, name: null })); } }} />
          </Field>
          <div className="pc-grid-2">
            <Field label="Product Image" opt="optional">
              <div className="pc-pb-type-toggle" style={{ marginBottom: 8, width: "fit-content" }}>
                {[{ key: "url", label: "Image URL" }, { key: "upload", label: "Upload" }].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`pc-pb-type-btn${imageMode === key ? " active" : ""}`}
                    onClick={() => { setImageMode(key); if (key === "url") { setImageFile(null); setImageError(""); } }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {imageMode === "url" ? (
                <input className="pc-input" placeholder="https://example.com/image.jpg" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} />
              ) : (
                <label style={{ display: "block", cursor: "pointer" }}>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,.jfif" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0] || null; if (f) { const allowed = ["image/jpeg","image/png","image/webp","image/jfif"]; if (!allowed.includes(f.type)) { setImageError("Invalid file type. Allowed: JPG, JPEG, PNG, WEBP, JFIF."); e.target.value = ""; return; } if (f.size > 5 * 1024 * 1024) { setImageError("File size exceeds 5 MB limit."); e.target.value = ""; return; } setImageError(""); setImageFile(f); setImageS3Url(""); } else { setImageFile(null); setImageError(""); } }} />
                  {imageFile ? (
                    /* newly selected file */
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--body-color)", border: "1.5px solid color-mix(in srgb,var(--btn-color) 40%,transparent)", borderRadius: 8 }}>
                      {imagePreviewUrl ? (
                        <img src={imagePreviewUrl} alt="Preview" style={{ width: 38, height: 38, borderRadius: 7, objectFit: "cover", border: "1px solid var(--body-color-less2)", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 38, height: 38, borderRadius: 7, background: "color-mix(in srgb,var(--btn-color) 12%,transparent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--btn-color)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                          </svg>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-color)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{imageFile.name}</div>
                        <div style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.5, marginTop: 2 }}>{(imageFile.size / 1024).toFixed(1)} KB · Click to change</div>
                      </div>
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImageFile(null); }} style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", color: "#f87171", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, lineHeight: 1 }}>×</button>
                    </div>
                  ) : imageS3Url ? (
                    /* existing S3 image (edit mode) */
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--body-color)", border: "1.5px solid color-mix(in srgb,var(--btn-color) 40%,transparent)", borderRadius: 8 }}>
                      <img src={imageS3Url} alt="Current" onError={(e) => { e.target.style.display = "none"; }} style={{ width: 38, height: 38, borderRadius: 7, objectFit: "cover", border: "1px solid var(--body-color-less2)", flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-color)" }}>Current image</div>
                        <div style={{ fontSize: 11, color: "var(--btn-color)", opacity: 0.8, marginTop: 2 }}>Click to replace with a new file</div>
                      </div>
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setImageS3Url(""); }} style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", color: "#f87171", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, lineHeight: 1 }}>×</button>
                    </div>
                  ) : (
                    /* empty drop zone */
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, height: 82, background: "var(--body-color)", border: "1.5px dashed color-mix(in srgb,var(--btn-color) 35%,transparent)", borderRadius: 8, transition: "border-color .2s, background .2s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--btn-color)"; e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 4%,transparent)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "color-mix(in srgb,var(--btn-color) 35%,transparent)"; e.currentTarget.style.background = "var(--body-color)"; }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--btn-color)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                        <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                      </svg>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--btn-color)" }}>Click to browse</div>
                        <div style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.45, marginTop: 2 }}>JPG, JPEG, PNG, WEBP, JFIF · Max 5 MB</div>
                      </div>
                    </div>
                  )}
                </label>
              )}
              {imageError && <div style={{ fontSize: 12, color: "#f87171", marginTop: 6 }}>{imageError}</div>}
            </Field>
            <Field label="Status">
              <div className="pc-radio-group">
                {(isEdit ? ["Active", "Draft", "Archived"] : ["Active", "Draft"]).map((s) => (
                  <label key={s} className="pc-radio-opt">
                    <input type="radio" name="pc-pstatus" value={s} checked={status === s} onChange={() => setStatus(s)} /> {s}
                  </label>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Description" opt="optional" error={errors.desc} full>
            <textarea className={`pc-input${errors.desc ? " error" : ""}`} placeholder="Describe your product…" value={desc} onChange={(e) => { setDesc(e.target.value); setErrors(ex => ({ ...ex, desc: null })); }} />
          </Field>
        </div>
      </div>

      <div className="pc-fs">
        <div className="pc-fs-title">
          <span>Attributes &amp; Variants <span className="pc-opt" style={{ textTransform: "none", letterSpacing: 0 }}>optional</span></span>
          <button className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setVariants((v) => [...v, { id: Date.now(), name: "", values: [] }])}>+ Add Attribute</button>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.65, marginBottom: 12 }}>
          Define attributes like <strong>size</strong>, <strong>color</strong>, etc. Each attribute maps directly to the API's <code style={{ fontSize: 11, background: "var(--body-color)", padding: "1px 5px", borderRadius: 4 }}>attributes</code> field.
        </div>
        {variants.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.55 }}>No attributes yet. Add size, color, material, etc.</div>
        ) : (
            variants.map((vo, i) => (
              <VOBlock key={vo.id} vo={vo} onChange={(upd) => setVariants((v) => v.map((x, j) => (j === i ? upd : x)))} onRemove={() => setVariants((v) => v.filter((_, j) => j !== i))} nameError={errors[`variantName_${i}`]} />
            ))
        )}
      </div>

      <div className="pc-fs">
        <div className="pc-fs-title">
          <span>Pricing</span>
          <button className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setPrices((p) => [...p, { id: Date.now(), priceType: "onetime", amount: "", currency: "USD", sku: "", isDefault: false, trackInventory: false, inventoryQty: "", variantObj: {}, billingInterval: "Month", billingEvery: "1", billingDuration: "no-end", totalCycles: "0", retryAttempts: "3", retryInterval: "Day", trialDays: "", extraCurrencies: [] }])}>+ Add Price</button>
        </div>
        {errors.prices && <div style={{ fontSize: 11, color: "#ef4444", marginBottom: 10 }}>{errors.prices}</div>}
        {prices.map((pb, i) => (
          <PriceBlock
            key={pb.id}
            pb={pb}
            isOnly={prices.length === 1}
            variants={variants}
            onUpdate={(upd) => setPrices((p) => p.map((x, j) => (j === i ? upd : x)))}
            onRemove={() => { setPrices((p) => { const next = p.filter((_, j) => j !== i); if (pb.isDefault && next.length > 0) next[0] = { ...next[0], isDefault: true }; return next; }); }}
            onSetDefault={(id) => setPrices((p) => p.map((x) => ({ ...x, isDefault: x.id === id })))}
            pErrors={{
              amount: errors[`price_${i}_amount`],
              extraAmounts: (pb.extraCurrencies || []).map((_, j) => errors[`price_${i}_extraAmount_${j}`]),
              sku: errors[`price_${i}_sku`],
              qty: errors[`price_${i}_qty`],
              billingEvery: errors[`price_${i}_billingEvery`],
              trialDays: errors[`price_${i}_trialDays`],
              retryAttempts: errors[`price_${i}_retryAttempts`],
              totalCycles: errors[`price_${i}_totalCycles`],
            }}
          />
        ))}
        {priceSummaryRows.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.7, color: "var(--text-color)", opacity: 0.6, marginBottom: 8 }}>Price Summary</div>
            <div className="pc-price-tbl">
              <table>
                <thead><tr><th>Variant</th><th>Price</th><th>Type</th><th>SKU</th><th>Inventory</th></tr></thead>
                <tbody>{priceSummaryRows.map((r, i) => (<tr key={i}><td>{r.variant}</td><td>{r.amount}</td><td>{r.type}</td><td>{r.sku}</td><td>{r.inventory}</td></tr>))}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="pc-fs">
        <div className="pc-fs-title">
          Metadata <span className="pc-opt" style={{ textTransform: "none", letterSpacing: 0 }}>optional</span>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.65, marginBottom: 12 }}>
          Extra key-value pairs sent as the <code style={{ fontSize: 11, background: "var(--body-color)", padding: "1px 5px", borderRadius: 4 }}>metadata</code> field (e.g. brand, material).
        </div>
        {attrs.map((a, i) => (
          <div key={a.id} style={{ marginBottom: 9 }}>
            <div className="pc-attr-row">
              <input
                className={`pc-input${errors[`attrKey_${i}`] ? " error" : ""}`}
                placeholder="Key (e.g. brand)"
                value={a.key}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^[a-zA-Z0-9_\+\-]*$/.test(v) && v.length <= 50) {
                    setAttrs((arr) => arr.map((x, j) => j === i ? { ...x, key: v } : x));
                    setErrors(ex => ({ ...ex, [`attrKey_${i}`]: null }));
                  }
                }}
                style={{ height: 36, fontSize: 13 }}
              />
              <input className="pc-input" placeholder="Value (e.g. NIKE)" value={a.val} onChange={(e) => setAttrs((arr) => arr.map((x, j) => j === i ? { ...x, val: e.target.value } : x))} style={{ height: 36, fontSize: 13 }} />
              <button className="pc-attr-rm" onClick={() => setAttrs((arr) => arr.filter((_, j) => j !== i))}>×</button>
            </div>
            {errors[`attrKey_${i}`] && <span style={{ fontSize: 11, color: "#ef4444", display: "block", marginTop: 3 }}>{errors[`attrKey_${i}`]}</span>}
          </div>
        ))}
        <button onClick={() => setAttrs((a) => [...a, { id: Date.now(), key: "", val: "" }])} style={{ background: "transparent", border: "1.5px dashed color-mix(in srgb,var(--btn-color) 45%,transparent)", color: "var(--btn-color)", fontFamily: "inherit", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, opacity: 0.85 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg> Add Metadata
        </button>
      </div>

      <div className="pc-form-footer">
        <button className="pc-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="pc-btn-save" disabled={saving || Object.values(errors).some(Boolean)} onClick={() => { if (validate()) { const removeCurrenciesWithPriceId = isEdit ? (() => { const removed = []; const currentPriceIds = new Set(prices.map(p => { if (p.id) { if (typeof p.id === 'string' && p.id.startsWith('price_')) return parseInt(p.id.replace('price_', '')); if (typeof p.id === 'number') return p.id; } return undefined; }).filter(Boolean)); originalPricesRef.current.forEach((origPrice) => { if (!origPrice?.id) return; const origCurrencies = new Set([origPrice.currency, ...origPrice.extraCurrencies || []].filter(Boolean)); let origPriceId = undefined; if (typeof origPrice.id === 'string' && origPrice.id.startsWith('price_')) { origPriceId = parseInt(origPrice.id.replace('price_', '')); } else if (typeof origPrice.id === 'number') { origPriceId = origPrice.id; } if (!origPriceId) return; const currentPrice = prices.find(p => { let pId = undefined; if (p.id) { if (typeof p.id === 'string' && p.id.startsWith('price_')) pId = parseInt(p.id.replace('price_', '')); else if (typeof p.id === 'number') pId = p.id; } return pId === origPriceId; }); if (!currentPrice) { removed.push({ priceId: origPriceId, currencies: Array.from(origCurrencies) }); } else { const currentCurrencies = new Set([currentPrice.currency, ...(currentPrice.extraCurrencies || []).map(ec => ec.currency)].filter(Boolean)); const removedCurrencies = Array.from(origCurrencies).filter(c => c && !currentCurrencies.has(c)); if (removedCurrencies.length > 0) { removed.push({ priceId: origPriceId, currencies: removedCurrencies }); } } }); return removed; })() : []; onSave({ id: initial?.id, name, status, description: desc, imageUrl: imageMode === "upload" ? (imageFile ? "" : imageS3Url) : imageUrl, imageFile: imageMode === "upload" ? imageFile : null, variants, prices, attrs, removeCurrenciesWithPriceId }); } }}>
          {saving ? (
            <><i className="mdi mdi-loading mdi-spin" /> Saving…</>
          ) : (
            <>
              {isEdit ? (
                // For edit mode
                status === "Draft" ? "Update as Draft" :
                  status === "Archived" ? "Update as Archived" :
                    "Update Product"
              ) : (
                // For create mode
                status === "Draft" ? "Save as Draft" :
                  status === "Archived" ? "Save as Archived" :
                    "Save Product"
              )}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CatalogForm
// ─────────────────────────────────────────────────────────────────
function CatalogForm({ initial, onSave, onCancel, saving, products = [] }) {
  const isEdit = !!initial?.id;
  const [name, setName] = React.useState(initial?.name || "");
  const [desc, setDesc] = React.useState(initial?.description || "");
  const [err, setErr] = React.useState("");
  const [selectedItems, setSelectedItems] = React.useState(initial?.productSelections || []);
  const [pendingProdId, setPendingProdId] = React.useState("");
  const [pendingPriceId, setPendingPriceId] = React.useState("");
  const [compatErr, setCompatErr] = React.useState("");

  const getOneTimePrices = (productId) => {
    const prod = products.find((p) => String(p.id) === String(productId) || String(p.productId) === String(productId));
    return (prod?.prices || []).filter((p) => p.priceType !== "subscription" && p.priceType !== "recurring");
  };

  const getPriceCurrencies = (productId, priceId) => {
    const prod = products.find((p) => String(p.id) === String(productId) || String(p.productId) === String(productId));
    if (!prod) return [];
    const price = (prod.prices || []).find((p) => String(p.id) === String(priceId));
    if (!price) return [];
    return [...new Set([price.currency, ...(price.extraCurrencies || []).map((e) => e.currency)].filter(Boolean))];
  };

  const commonCurrencies = React.useMemo(() => {
    if (!selectedItems.length) return null;
    return selectedItems.reduce((acc, item) => acc === null ? [...item.currencies] : acc.filter((c) => item.currencies.includes(c)), null) ?? [];
  }, [selectedItems]);

  const pendingCurrencies = React.useMemo(() => {
    if (!pendingProdId || !pendingPriceId) return [];
    return getPriceCurrencies(pendingProdId, pendingPriceId);
  }, [pendingProdId, pendingPriceId, products]);

  const getPriceType = (productId, priceId) => {
    const prod = products.find((p) => String(p.id) === String(productId) || String(p.productId) === String(productId));
    if (!prod) return null;
    const price = (prod.prices || []).find((p) => String(p.id) === String(priceId));
    return price?.priceType || null;
  };

  const handleAddItem = () => {
    if (!pendingProdId || !pendingPriceId) { setCompatErr("Please select a product and a price."); return; }
    if (selectedItems.some((i) => String(i.productId) === String(pendingProdId) && String(i.priceId) === String(pendingPriceId))) { setCompatErr("This product + price combination is already added."); return; }
    const pendingPriceType = getPriceType(pendingProdId, pendingPriceId);
    if (selectedItems.length > 0 && selectedItems[0].priceType && pendingPriceType) {
      if (selectedItems[0].priceType !== pendingPriceType) {
        const typeLabel = (t) => t === "subscription" ? "Subscription" : "One-time";
        setCompatErr(`Price type mismatch. Catalogue already has ${typeLabel(selectedItems[0].priceType)} prices. Selected price is ${typeLabel(pendingPriceType)}.`);
        return;
      }
    }
    if (commonCurrencies !== null && commonCurrencies.length > 0) {
      const overlap = pendingCurrencies.filter((c) => commonCurrencies.includes(c));
      if (!overlap.length) { setCompatErr(`Incompatible currencies. Current selection supports: ${commonCurrencies.join(", ")}. This price has: ${pendingCurrencies.join(", ")}.`); return; }
    }
    const prod = products.find((p) => String(p.id) === String(pendingProdId) || String(p.productId) === String(pendingProdId));
    const selectedPrice = (prod?.prices || []).find((p) => String(p.id) === String(pendingPriceId));
    const inventoryQty = selectedPrice?.trackInventory ? (selectedPrice?.inventoryQty ?? "") : null;
    const itemQuantity = prod?.itemQuantity ?? null;
    setCompatErr("");
    setSelectedItems((prev) => [...prev, { id: Date.now(), productId: String(prod?.productId || prod?.id || pendingProdId), productName: prod?.name || pendingProdId, priceId: pendingPriceId, priceType: getPriceType(pendingProdId, pendingPriceId), currencies: pendingCurrencies, inventoryQty, itemQuantity }]);
    setPendingProdId("");
    setPendingPriceId("");
  };

  const removeItem = (id) => { setSelectedItems((prev) => prev.filter((i) => i.id !== id)); setCompatErr(""); };

  const eligibleProducts = products.filter((p) => (p.prices || []).some((pr) => pr.priceType !== "subscription" && pr.priceType !== "recurring"));
  const oneTimePricesForPending = getOneTimePrices(pendingProdId);

  return (
    <div className="pc-view-in">
      <BackLink onClick={onCancel} label="Back to Catalogues" />
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-color)", marginBottom: 20, letterSpacing: "-.3px" }}>
        {isEdit ? `Edit — ${initial.name}` : "New Catalogue"}
      </div>

      <div className="pc-fs">
        <div className="pc-fs-title">Basic Info</div>
        <div className="pc-grid-form" style={{ gap: 16 }}>
          <Field label="Catalogue Name" req error={err}>
            <input className={`pc-input${err ? " error" : ""}`} placeholder="e.g. Summer Collection" value={name} onChange={(e) => { setName(e.target.value); setErr(""); }} />
          </Field>
          <Field label="Description" opt="optional">
            <textarea className="pc-input" placeholder="Brief description…" style={{ height: 80 }} value={desc} onChange={(e) => setDesc(e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="pc-fs">
        <div className="pc-fs-title">
          <span>Products</span>
          {commonCurrencies !== null && commonCurrencies.length > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text-color)", opacity: 0.6, textTransform: "none", letterSpacing: 0 }}>Active currencies:</span>
              {commonCurrencies.map((c) => (<span key={c} style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: "color-mix(in srgb,var(--btn-color) 15%,transparent)", color: "var(--btn-color)" }}>{c}</span>))}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.65, marginBottom: 14 }}>
          Attach products to this catalogue. Only <strong>one-time</strong> prices are shown. All selected prices must share at least one common currency.
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 12, flexWrap: "wrap" }}>
          <Field label="Product">
            <select className="pc-sel" style={{ minWidth: 160, maxWidth: "100%" }} value={pendingProdId} onChange={(e) => { setPendingProdId(e.target.value); setPendingPriceId(""); setCompatErr(""); }}>
              <option value="">— Select product —</option>
              {eligibleProducts.map((p) => (<option key={p.id || p.productId} value={p.id || p.productId}>{p.name}</option>))}
            </select>
          </Field>

          <Field label="Price ID">
            <select className="pc-sel" style={{ minWidth: 160, maxWidth: "100%" }} value={pendingPriceId} onChange={(e) => { setPendingPriceId(e.target.value); setCompatErr(""); }} disabled={!pendingProdId}>
              <option value="">— Select price —</option>
              {oneTimePricesForPending.map((p) => { const num = String(p.id).replace("price_", ""); return (<option key={p.id} value={p.id}>Price #{num} — {p.currency} {parseFloat(p.amount || 0).toFixed(2)}</option>); })}
            </select>
          </Field>

          {pendingPriceId && pendingCurrencies.length > 0 && (
            <div style={{ paddingBottom: 4 }}>
              <div style={{ fontSize: 11, color: "var(--text-color)", opacity: 0.6, marginBottom: 5 }}>Currencies</div>
              <div style={{ display: "flex", gap: 5 }}>
                {pendingCurrencies.map((c) => { const compatible = commonCurrencies === null || commonCurrencies.includes(c); return (<span key={c} style={{ padding: "3px 9px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: compatible ? "rgba(16,185,129,.15)" : "rgba(239,68,68,.12)", color: compatible ? "#10b981" : "#f87171", display: "inline-flex", alignItems: "center", gap: 4 }}>{compatible ? <i className="mdi mdi-check-circle-outline" style={{ fontSize: 11 }} /> : <i className="mdi mdi-close-circle-outline" style={{ fontSize: 11 }} />}{c}</span>); })}
              </div>
            </div>
          )}

          <button className="pc-btn pc-btn-primary pc-btn-sm" style={{ marginBottom: 0, whiteSpace: "nowrap", height: "40px" }} onClick={handleAddItem} disabled={!pendingProdId || !pendingPriceId}>+ Add</button>
        </div>

        {compatErr && (
          <div style={{ fontSize: 12, color: "#f87171", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 7, padding: "9px 13px", marginBottom: 12, display: "flex", alignItems: "flex-start", gap: 7 }}>
            <i className="mdi mdi-alert-outline" style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }} />{compatErr}
          </div>
        )}

        {selectedItems.length > 0 ? (
          <div style={{ background: "var(--body-color)", border: "1px solid var(--body-color-less2)", borderRadius: 9, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--body-color-less2)", background: "color-mix(in srgb,var(--btn-color) 6%,transparent)" }}>
                  {["Product", "Quantity", "Price ID", "Currencies", ""].map((h) => (<th key={h} style={{ padding: "9px 13px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.65, color: "var(--text-color)", opacity: 0.7, textAlign: "left" }}>{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((item, idx) => (
                  <tr key={item.id} style={{ borderBottom: idx < selectedItems.length - 1 ? "1px solid var(--body-color-less2)" : "none" }}>
                    <td style={{ padding: "10px 13px", fontSize: 13, fontWeight: 600, color: "var(--text-color)" }}>{item.productName}</td>
                    <td style={{ padding: "10px 13px", fontSize: 13, color: "var(--text-color)" }}>
                      {item.itemQuantity !== null && item.itemQuantity !== undefined
                        ? <span style={{ fontWeight: 600 }}>{item.itemQuantity}</span>
                        : <span style={{ opacity: 0.45, fontSize: 12 }}>—</span>}
                    </td>

                    <td style={{ padding: "10px 13px" }}>
                      <div style={{ fontSize: 12, color: "var(--text-color)", opacity: 0.75, fontFamily: "monospace" }}>#{String(item.priceId).replace("price_", "")}</div>
                      {item.priceDisplay && <div style={{ fontSize: 11, color: "var(--btn-color)", fontFamily: "inherit", marginTop: 2, fontWeight: 600 }}>{item.priceDisplay}</div>}
                    </td>
                    <td style={{ padding: "10px 13px" }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{item.currencies.map((c) => (<span key={c} style={{ padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 700, background: "color-mix(in srgb,var(--btn-color) 12%,transparent)", color: "var(--btn-color)" }}>{c}</span>))}</div>
                    </td>
                    <td style={{ padding: "10px 13px", textAlign: "right" }}>
                      <button onClick={() => removeItem(item.id)} style={{ background: "transparent", border: "1px solid rgba(239,68,68,.3)", color: "#ef4444", cursor: "pointer", padding: "6px 10px", borderRadius: 6, transition: "all .25s cubic-bezier(0.4, 0, 0.2, 1)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }} onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239,68,68,.08)"; e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(239,68,68,.15)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(239,68,68,.3)"; e.currentTarget.style.boxShadow = "none"; }}><i className="mdi mdi-trash-can-outline" style={{ fontSize: 15 }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ padding: "8px 13px", borderTop: "1px solid var(--body-color-less2)", display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
              {commonCurrencies !== null && commonCurrencies.length > 0 ? (
                <>
                  <i className="mdi mdi-information-outline" style={{ color: "var(--btn-color)", fontSize: 13 }} />
                  <span style={{ color: "var(--text-color)", opacity: 0.65 }}>Catalogue will operate on common currencies:</span>
                  {commonCurrencies.map((c) => (<span key={c} style={{ padding: "1px 7px", borderRadius: 5, fontWeight: 700, background: "rgba(16,185,129,.15)", color: "#10b981" }}>{c}</span>))}
                </>
              ) : (
                <>
                  <i className="mdi mdi-alert" style={{ color: "#f87171", fontSize: 13 }} />
                  <span style={{ color: "#f87171" }}>No common currencies across all selected prices — please review your selection.</span>
                </>
              )}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.45, textAlign: "center", padding: "18px 0", border: "1.5px dashed var(--body-color-less2)", borderRadius: 9 }}>No products added yet</div>
        )}
      </div>

      <div className="pc-form-footer">
        <button className="pc-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="pc-btn-save" disabled={saving} onClick={() => { if (!name.trim()) { setErr("Catalogue name is required"); return; } if (!selectedItems.length) { setCompatErr("At least one product must be added to the catalogue"); return; } onSave({ id: initial?.id, name, description: desc, productSelections: selectedItems }); }}>
          {saving ? <><i className="mdi mdi-loading mdi-spin" /> Saving…</> : isEdit ? "Update Catalogue" : "Save Catalogue"}
        </button>
      </div>
    </div>
  );
}

function mapCatalogProductsToSelections(catalogData) {
  const selections = [];
  (catalogData?.products || []).forEach((p) => {
    const prices = p.prices || [];
    const hasMultiple = prices.length > 1;
    prices.forEach((pr) => {
      if (hasMultiple && Number(pr.selectedPriceId) !== 1) return;
      const currencies = (pr.currencies || []).map((c) => c.currency);
      const defaultCur = (pr.currencies || []).find((c) => c.isDefault) || pr.currencies?.[0];
      selections.push({
        id: Date.now() + Math.random(),
        productId: String(p.productId),
        productName: p.name || p.productId,
        priceId: `price_${pr.priceId}`,
        priceType: pr.priceType === "recurring" ? "subscription" : "onetime",
        priceDisplay: defaultCur ? `${defaultCur.currency} ${parseFloat(defaultCur.amount).toFixed(2)}` : "",
        currencies,
        inventoryQty: pr.inventory?.track ? (pr.inventory?.quantity ?? "") : null,
        itemQuantity: p.itemQuantity ?? null,
      });
    });
  });
  return selections;
}

// ─────────────────────────────────────────────────────────────────
// Root Component
// ─────────────────────────────────────────────────────────────────
export default function ProductCatalog() {
  const merchantId = getMid();
  const [catalogs, setCatalogs] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catSaving, setCatSaving] = useState(false);
  const [catDeleting, setCatDeleting] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productSaving, setProductSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [prodView, setProdView] = useState("list");
  const [catView, setCatView] = useState("list");
  const [editProd, setEditProd] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [confirm, setConfirm] = useState({ open: false, title: "", body: "", cb: null });
  const permission = currentPageAccess();
  const isReadOnly = permission === "READ";
  const guardWrite = (fn) => (...args) => {
    if (isReadOnly) {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    return fn(...args);
  };

  const loadCatalogs = () => {
    if (!merchantId) { setCatLoading(false); return; }
    setCatLoading(true);
    apiGetCatalogs(merchantId).then((res) => {
      if (!res.status) { showToast("error", res.message || "Failed to load catalogues"); return; }
      setCatalogs((res.data || []).map((c) => ({ id: c.id, name: c.catalogName, description: c.description || "" })));
    }).catch((err) => showHttpError(err)).finally(() => setCatLoading(false));
  };

  const loadProducts = async (opts = {}) => {
    if (!merchantId) { setProductsLoading(false); return; }
    setProductsLoading(true);
    try {
      const searchTerm = opts.search !== undefined ? opts.search : search;
      const statusVal = opts.status !== undefined ? opts.status : statusFilter;
      const pageVal = opts.page !== undefined ? opts.page : page;
      const sizeVal = opts.size !== undefined ? opts.size : pageSize;
      const params = new URLSearchParams({ merchantId: String(merchantId) });
      params.append("page", String(pageVal));
      params.append("size", String(sizeVal));
      params.append("status", statusVal || "ALL");
      if (searchTerm && searchTerm.trim()) params.append("searchTerm", searchTerm.trim());
      const res = await fetch(`${API_BASE}/shopping/products?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw { response: { status: res.status } };
      const data = await res.json();
      if (!data.status) { showToast("error", data.message || "Failed to load products"); setProducts([]); setTotalProducts(0); return; }
      const d = data.data || {};
      const total = d.totalElements ?? d.totalItems ?? d.total ?? d.totalCount ?? (d.products || []).length;
      setTotalProducts(total);
      const allProds = (data.data?.products || []).map((p) => ({
        id: p.productId,
        productId: p.productId,
        name: p.name || "Unnamed",
        description: p.description || "",
        imageUrl: p.imageUrl || "",
        imageS3Key: p.imageS3Key || "",
        catalogId: null,
        status: p.status === "ACTIVE" ? "Active" : p.status === "DRAFT" ? "Draft" : "Archived",
        category: "",
        variants: Object.entries(p.attributes || {}).map(([k, v], i) => ({ id: i, name: k, values: Array.isArray(v) ? v : [String(v)] })),
        prices: (p.prices || []).map((pr) => {
          const currencies = pr.currencies || [];
          const primaryCur = currencies.find((c) => c.default === true) || currencies[0];
          const primaryCode = primaryCur?.currency || "USD";
          const isSub = pr.priceType === "recurring";
          return {
            id: `price_${pr.priceId}`,
            // In loadProducts function, update the priceType mapping:
            priceType: isSub ? "subscription" : "onetime",  
            billingInterval: pr.intervalType || "Month",
            billingEvery: String(pr.intervalCount || 1),
            amount: String(primaryCur?.amount || 0),
            currency: primaryCode,
            sku: pr.metadata?.sku || "",
            trackInventory: pr.metadata?.inventory?.track || false,
            inventoryQty: String(pr.metadata?.inventory?.quantity || ""),
            variantObj: pr.metadata?.variant || {},
            totalCycles: pr.totalCycles ? String(pr.totalCycles) : "",
            billingDuration: pr.totalCycles ? "fixed" : "no-end",
            retryAttempts: pr.retryAttempts != null ? String(pr.retryAttempts) : "3",
            retryInterval: pr.retryInterval || "Day",
            trialDays: pr.trialDays !== undefined && pr.trialDays !== null ? String(pr.trialDays) : isSub ? "0" : "",
            extraCurrencies: currencies.filter((c) => { const anyDefault = currencies.some((x) => x.default === true); if (anyDefault) return c.default !== true; return c.currency !== primaryCode; }).map((c, i) => ({ id: i, currency: c.currency, amount: String(c.amount) })),
          };
        }),
        attrs: Object.entries(p.metadata || {}).map(([key, val], i) => ({ id: i, key, val: String(val) })),
      }));
      setProducts(allProds);
    } catch (err) { showHttpError(err); setProducts([]); } finally { setProductsLoading(false); }
  };

  useEffect(() => { loadCatalogs(); }, [merchantId]);

  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  useEffect(() => {
    if (!merchantId) return;
    const t = setTimeout(() => {
      loadProducts({ search, status: statusFilter, page, size: pageSize });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, search, statusFilter, page, pageSize]);

  const isFormOpen = (activeTab === "products" && prodView === "form") || (activeTab === "catalogues" && catView === "form");
  const getCatName = (id) => catalogs.find((c) => c.id === id)?.name || "—";

  const handleSaveCat = async (form) => {
    setCatSaving(true);
    try {
      let res, catalogId;
      if (form.id) {
        res = await apiUpdateCatalog(form.id, { merchantId: parseInt(merchantId), catalogName: form.name, description: form.description || "" });
        if (!res.status) { showToast("error", res.message || "Failed to save catalogue"); return; }
        catalogId = form.id;
        const originalIds = new Set((editCat?.productSelections || []).map((i) => `${i.productId}__${i.priceId}`));
        const newIds = new Set((form.productSelections || []).map((i) => `${i.productId}__${i.priceId}`));
        const toAdd = (form.productSelections || []).filter((item) => !originalIds.has(`${item.productId}__${item.priceId}`));
        const toDelete = (editCat?.productSelections || []).filter((item) => !newIds.has(`${item.productId}__${item.priceId}`));
        for (const item of toAdd) { const rawPriceId = String(item.priceId).replace("price_", ""); const addRes = await apiAddProductToCart(catalogId, item.productId, rawPriceId, "ADD"); if (!addRes.success) { showToast("error", `${item.productName}: ${addRes.message || "Failed to add product"}`); return; } }
        for (const item of toDelete) { const rawPriceId = String(item.priceId).replace("price_", ""); const delRes = await apiAddProductToCart(catalogId, item.productId, rawPriceId, "DELETE"); if (!delRes.success) { showToast("error", `${item.productName}: ${delRes.message || "Failed to remove product"}`); return; } }
      } else {
        res = await apiCreateCatalog({ merchantId: parseInt(merchantId), catalogName: form.name, description: form.description || "" });
        if (!res.status) { showToast("error", res.message || "Failed to save catalogue"); return; }
        catalogId = res.data?.id;
        for (const item of form.productSelections || []) { const rawPriceId = String(item.priceId).replace("price_", ""); const addRes = await apiAddProductToCart(catalogId, item.productId, rawPriceId, "ADD"); if (!addRes.success) { showToast("error", `${item.productName}: ${addRes.message || "Failed to add product"}`); return; } }
      }
      showToast("success", res.message || (form.id ? "Catalogue updated" : "Catalogue created"));
      loadCatalogs();
      setCatView("list");
      setEditCat(null);
    } catch (e) { showHttpError(e); } finally { setCatSaving(false); }
  };

  const handleDeleteCat = async (cat) => {
    setCatDeleting(cat.id);
    try {
      const res = await apiDeleteCatalog(cat.id, merchantId);
      if (!res.status) { showToast("error", res.message || "Failed to delete catalogue"); return; }
      showToast("success", res.message || "Catalogue deleted");
      loadCatalogs();
    } catch (e) { showHttpError(e); } finally { setCatDeleting(null); closeConfirm(); }
  };

  const handleSaveProd = async (form) => {
    setProductSaving(true);
    try {
      let res;
      let productId;
      if (form.id) {
        const payload = buildUpdatePayload(merchantId, form);
        res = await apiUpdateProduct(form.id, payload);
        if (!res.status) { showToast("error", res.message || "Failed to update product"); return; }
        productId = form.id;
      } else {
        const payload = buildProductPayload(merchantId, form);
        res = await apiRegisterProducts(payload);
        if (!res.status) { showToast("error", res.message || "Failed to register product"); return; }
        const registered = res.data?.products?.[0];
        if (registered && registered.status === "error") { showToast("error", `Product registration failed: ${registered.productId}`); return; }
        productId = registered?.productId;
      }
      if (form.imageFile && productId) {
        try {
          await apiUploadProductImage(merchantId, productId, form.imageFile);
        } catch (imgErr) {
          showToast("error", "Product saved but image upload failed");
        }
      }
      showToast("success", res.message || (form.id ? "Product updated successfully" : "Product registered successfully"));
      setProdView("list");
      setEditProd(null);
      loadProducts();
    } catch (e) { showHttpError(e); } finally { setProductSaving(false); }
  };

  const handleDeleteProd = async (p) => {
    try {
      const res = await apiDeleteProduct(merchantId, p.id);
      if (!res.status) { showToast("error", res.message || "Failed to delete product"); return; }
      showToast("success", res.message || "Product deleted successfully");
      loadProducts();
    } catch (e) { showHttpError(e); } finally { closeConfirm(); }
  };

  const confirmAct = (title, body, cb) => setConfirm({ open: true, title, body, cb });
  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const filteredProds = products.filter((p) => {
    if (catFilter && String(p.catalogId) !== String(catFilter)) return false;
    return true;
  });

  return (
    <Fragment>
      <style>{CSS}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="pc-page">

            {/* Header - only show when not in form view */}
            {!isFormOpen && (
              <div className="pc-hdr">
                <div>
                  <h2 className="pc-title">Products</h2>
                  <p className="pc-sub">Manage products, pricing, and catalogues</p>
                </div>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <button
                    className="pc-btn pc-btn-primary"
                    disabled={isReadOnly}
                    style={isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : null}
                    onClick={guardWrite(() => { if (activeTab === "products") { setEditProd(null); setProdView("form"); } else { setEditCat(null); setCatView("form"); } })}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    {activeTab === "products" ? "Add Product" : "Add Catalogue"}
                  </button>
                </div>
              </div>
            )}

            {/* Tab Bar - only show when not in form view */}
            {!isFormOpen && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 24, background: "var(--body-color)", borderRadius: 12, padding: 5, width: "fit-content", border: "1px solid var(--body-color-less2)" }}>
                {[
                  { key: "products", label: "Products", icon: "mdi-package-variant", count: totalProducts },
                  { key: "catalogues", label: "Catalogues", icon: "mdi-folder-multiple-outline", count: catalogs.length },
                ].map((t) => {
                  const a = activeTab === t.key;
                  return (
                    <button key={t.key} onClick={() => { setActiveTab(t.key); setProdView("list"); setEditProd(null); setCatView("list"); setEditCat(null); }} style={{ height: 36, padding: "0 18px", border: "none", borderRadius: 8, background: a ? "var(--btn-color)" : "transparent", fontFamily: "inherit", fontSize: 13, fontWeight: a ? 600 : 500, color: a ? "#fff" : "var(--text-color)", opacity: a ? 1 : 0.6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 7, transition: "all .15s", boxShadow: a ? "0 2px 8px rgba(0,0,0,0.2)" : "none" }}>
                      <i className={`mdi ${t.icon}`} style={{ fontSize: 14 }} />
                      {t.label}
                      <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "1px 8px", background: a ? "rgba(255,255,255,0.2)" : "color-mix(in srgb,var(--btn-color) 15%,transparent)", color: a ? "#fff" : "var(--btn-color)" }}>{t.count}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Products Tab */}
            {/* Products Tab */}
            {activeTab === "products" && (
              prodView === "form" ? (
                <ProductForm initial={editProd} onSave={handleSaveProd} onCancel={() => { setProdView("list"); setEditProd(null); }} saving={productSaving} />
              ) : productsLoading ? (
                <div style={{ textAlign: "center", padding: "60px 20px" }}><i className="mdi mdi-loading mdi-spin" style={{ fontSize: 28, color: "var(--btn-color)" }} /><div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.6, marginTop: 10 }}>Loading products…</div></div>
              ) : (
                <>
                  <div className="pc-topbar">
                    <div className="pc-search-wrap">
                      <svg className="pc-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                      <input className="pc-search-input" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    {/* <select className="pc-filter-sel" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}><option value="">All Catalogues</option>{catalogs.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}</select> */}
                    <select className="pc-filter-sel" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="ALL">All</option><option value="ACTIVE">Active</option><option value="DRAFT">Draft</option><option value="ARCHIVED">Archived</option></select>
                  </div>

                  {/* Scrollable table container */}
                  <div style={{ maxHeight: "340px", overflowY: "auto", overflowX: "auto", borderRadius: "12px", border: "1px solid var(--body-color-less2)", WebkitOverflowScrolling: "touch" }}>
                    <div className="pc-tbl-wrap" style={{ border: "none", borderRadius: "0", overflow: "visible" }}>
                      <table style={{ width: "100%", minWidth: "650px", borderCollapse: "collapse" }}>
                        <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                          <tr style={{ background: "var(--body-color-less)", borderBottom: "1px solid var(--body-color-less2)" }}>
                            <th style={{ padding: "12px 16px", fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".65px", color: "var(--text-color)", opacity: 0.7, textAlign: "left", position: "sticky", top: 0, background: "var(--body-color-less)" }}>Product</th>
                            <th style={{ padding: "12px 16px", fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".65px", color: "var(--text-color)", opacity: 0.7, textAlign: "left", position: "sticky", top: 0, background: "var(--body-color-less)" }}>Pricing</th>
                            <th style={{ padding: "12px 16px", fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".65px", color: "var(--text-color)", opacity: 0.7, textAlign: "left", position: "sticky", top: 0, background: "var(--body-color-less)" }}>Stock</th>
                            <th style={{ padding: "12px 16px", fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".65px", color: "var(--text-color)", opacity: 0.7, textAlign: "left", position: "sticky", top: 0, background: "var(--body-color-less)" }}>Status</th>
                            <th style={{ padding: "12px 16px", fontSize: "12px", fontWeight: "700", textTransform: "uppercase", letterSpacing: ".65px", color: "var(--text-color)", opacity: 0.7, textAlign: "left", position: "sticky", top: 0, background: "var(--body-color-less)" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProds.length === 0 ? (
                            <tr>
                              <td colSpan={5}>
                                <div className="pc-empty">
                                  <div className="pc-empty-icon">📦</div>
                                  <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-color)" }}>No products found</div>
                                  <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.55 }}>{search || catFilter || statusFilter ? "Try adjusting your filters" : "Add your first product to get started"}</div>
                                  {!search && !catFilter && !statusFilter && (<button className="pc-btn pc-btn-primary" disabled={isReadOnly} style={{ marginTop: 4, ...(isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : {}) }} onClick={guardWrite(() => { setEditProd(null); setProdView("form"); })}>+ Create Product</button>)}
                                </div>
                              </td>
                            </tr>
                          ) : (
                            filteredProds.map((p) => (
                              <tr key={p.id} onClick={guardWrite(() => { setEditProd(p); setProdView("form"); })} style={{ cursor: isReadOnly ? "not-allowed" : "pointer", borderBottom: "1px solid var(--body-color-less2)", transition: "background .15s" }} onMouseEnter={(e) => { if (!e.currentTarget.style.background) e.currentTarget.style.background = "color-mix(in srgb,var(--btn-color) 4%,transparent)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                                <td style={{ padding: "13px 16px", fontSize: "13px", verticalAlign: "middle" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    {p.imageUrl && <img src={p.imageUrl} alt={p.name} onError={(e) => { e.target.style.display = "none"; }} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", border: "1px solid var(--body-color-less2)", flexShrink: 0 }} />}
                                    <div>
                                      <div className="pc-td-name">{p.name}</div>
                                      {p.productId && <div className="pc-td-sub" style={{ fontFamily: "monospace" }}>{p.productId}</div>}
                                    </div>
                                  </div>
                                </td>
                                <td style={{ padding: "13px 16px", fontSize: "12px", color: "var(--text-color)", opacity: 0.8, lineHeight: 1.6, verticalAlign: "middle" }}>{pricingSummary(p.prices)}</td>
                                <td style={{ padding: "13px 16px", verticalAlign: "middle" }}><StockBadge qty={p.prices?.some((pr) => pr.trackInventory) ? p.prices[0]?.inventoryQty : undefined} /></td>
                                <td style={{ padding: "13px 16px", verticalAlign: "middle" }}><span className={`pc-badge ${(p.status || "active").toLowerCase()}`}>{p.status || "Active"}</span></td>
                                <td style={{ padding: "13px 16px", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
                                  <div className="pc-td-actions">
                                    <button className="pc-btn-outline" disabled={isReadOnly} style={isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : null} onClick={guardWrite(() => { setEditProd(p); setProdView("form"); })}><i className="mdi mdi-pencil-outline" style={{ fontSize: 12 }} /> Edit</button>
                                    <button className="pc-btn-danger" disabled={isReadOnly} style={isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : null} onClick={guardWrite(() => confirmAct("Delete Product", `Remove <strong>${p.name}</strong>? This cannot be undone.`, () => handleDeleteProd(p)))}><i className="mdi mdi-delete-outline" style={{ fontSize: 12 }} /> Delete</button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {totalProducts > 0 && (() => {
                    const totalPages = Math.max(1, Math.ceil(totalProducts / pageSize));
                    const start = (page - 1) * pageSize + 1;
                    const end = Math.min(page * pageSize, totalProducts);
                    const btnStyle = (disabled) => ({
                      height: 32, minWidth: 32, padding: "0 10px", borderRadius: 7,
                      border: "1px solid var(--body-color-less2)",
                      background: "var(--body-color-less)",
                      color: "var(--text-color)", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
                      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.4 : 1,
                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                    });
                    return (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginTop: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                          <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => setPage(1)} title="First">
                            <i className="mdi mdi-chevron-double-left" />
                          </button>
                          <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="Previous">
                            <i className="mdi mdi-chevron-left" />
                          </button>
                          <span style={{ fontSize: 12, color: "var(--text-color)", padding: "0 8px" }}>
                            Page <strong>{page}</strong> of {totalPages}
                          </span>
                          <button style={btnStyle(page >= totalPages)} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} title="Next">
                            <i className="mdi mdi-chevron-right" />
                          </button>
                          <button style={btnStyle(page >= totalPages)} disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last">
                            <i className="mdi mdi-chevron-double-right" />
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Create Payment Button - Below Products Table (always visible) */}
                  {(products.length > 0) && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                      <button
                        className="pc-btn"
                        disabled={isReadOnly}
                        onClick={guardWrite(() => { window.open(`${window.location.origin}/create-payment`, "_blank"); })}
                        style={{ background: "#10b981", color: "#fff", height: "40px", padding: "0 20px", borderRadius: "8px", fontFamily: "inherit", fontSize: "13px", fontWeight: "500", cursor: isReadOnly ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "8px", transition: "all .15s", whiteSpace: "nowrap", border: "none", opacity: isReadOnly ? 0.5 : 1 }}
                        onMouseEnter={(e) => { if (isReadOnly) return; e.currentTarget.style.background = "#059669"; e.currentTarget.style.transform = "scale(0.98)"; }}
                        onMouseLeave={(e) => { if (isReadOnly) return; e.currentTarget.style.background = "#10b981"; e.currentTarget.style.transform = "scale(1)"; }}
                      >
                        Create Payment
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                      </button>
                    </div>
                  )}
                </>
              )
            )}

            {/* Catalogues Tab */}
            {activeTab === "catalogues" && (
              catView === "form" ? (
                <CatalogForm initial={editCat} onSave={handleSaveCat} onCancel={() => { setCatView("list"); setEditCat(null); }} saving={catSaving} products={products} />
              ) : catLoading ? (
                <div style={{ textAlign: "center", padding: "60px 20px" }}><i className="mdi mdi-loading mdi-spin" style={{ fontSize: 28, color: "var(--btn-color)" }} /><div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.6, marginTop: 10 }}>Loading catalogues…</div></div>
              ) : catalogs.length === 0 ? (
                <div className="pc-tbl-wrap"><div className="pc-empty"><div className="pc-empty-icon">🗂️</div><div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-color)" }}>No catalogues yet</div><div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.55 }}>Create a catalogue to group your products</div><button className="pc-btn pc-btn-primary" disabled={isReadOnly} style={{ marginTop: 4, ...(isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : {}) }} onClick={guardWrite(() => { setEditCat(null); setCatView("form"); })}>+ Create Catalogue</button></div></div>
              ) : (
                <>
                  <div className="pc-cat-grid">
                    {catalogs.map((c) => {
                      const deleting = catDeleting === c.id;
                      const openEdit = async () => {
                        try {
                          const res = await apiGetCatalogProducts(merchantId, c.id);
                          const productSelections = res.status ? mapCatalogProductsToSelections(res.data?.catalogs?.[0]) : [];
                          setEditCat({ ...c, productSelections });
                        } catch { setEditCat(c); }
                        setCatView("form");
                      };
                      return (
                        <div key={c.id} className="pc-cat-card" style={isReadOnly ? { cursor: "not-allowed" } : null} onClick={guardWrite(openEdit)}>
                          <div className="pc-cat-banner"><div className="pc-cat-banner-ring" /><div className="pc-cat-banner-ring2" /><i className="mdi mdi-folder-multiple-outline pc-cat-banner-icon" /></div>
                          <div className="pc-cat-body"><h3 className="pc-cat-name">{c.name}</h3><p className="pc-cat-desc">{c.description || "No description"}</p><div className="pc-cat-meta"><span className="pc-cat-meta-pill"><i className="mdi mdi-folder-outline" style={{ fontSize: 10 }} /> Catalogue</span></div></div>
                          <div className="pc-cat-divider" />
                          <div className="pc-cat-footer" onClick={(e) => e.stopPropagation()}><div className="pc-cat-actions"><button className="pc-cat-btn pc-cat-btn-edit" disabled={isReadOnly} style={isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : null} onClick={guardWrite(openEdit)}><i className="mdi mdi-pencil-outline" style={{ fontSize: 12 }} /> Edit</button><button className="pc-cat-btn pc-cat-btn-delete" disabled={deleting || isReadOnly} style={isReadOnly ? { opacity: 0.5, cursor: "not-allowed" } : null} onClick={guardWrite(() => confirmAct("Delete Catalogue", `Remove <strong>${c.name}</strong>? This cannot be undone.`, () => handleDeleteCat(c)))}>{deleting ? <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 12 }} /> : <><i className="mdi mdi-delete-outline" style={{ fontSize: 12 }} /> Delete</>}</button></div></div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Create Payment Button - Below Catalogues Grid */}
                  {(catalogs.length > 0) && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                      <button
                        className="pc-btn"
                        disabled={isReadOnly}
                        onClick={guardWrite(() => { window.open(`${window.location.origin}/create-payment`, "_blank"); })}
                        style={{ background: "#10b981", color: "#fff", height: "40px", padding: "0 20px", borderRadius: "8px", fontFamily: "inherit", fontSize: "13px", fontWeight: "500", cursor: isReadOnly ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "8px", transition: "all .15s", whiteSpace: "nowrap", border: "none", opacity: isReadOnly ? 0.5 : 1 }}
                        onMouseEnter={(e) => { if (isReadOnly) return; e.currentTarget.style.background = "#059669"; e.currentTarget.style.transform = "scale(0.98)"; }}
                        onMouseLeave={(e) => { if (isReadOnly) return; e.currentTarget.style.background = "#10b981"; e.currentTarget.style.transform = "scale(1)"; }}
                      >
                        Create Payment
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                              </svg>
                      </button>
                    </div>
                  )}
                </>
              )
            )}
          </div>
          <Footer />
        </div>
      </div>

      {/* Confirm Modal */}
      <div className={`pc-confirm-bg${confirm.open ? " open" : ""}`}>
        <div className="pc-confirm-box">
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-color)", marginBottom: 8 }}>{confirm.title}</div>
          <div style={{ fontSize: 13, color: "var(--text-color)", opacity: 0.7, marginBottom: 20 }} dangerouslySetInnerHTML={{ __html: confirm.body }} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="pc-btn-cancel" style={{ height: 36, fontSize: 13 }} onClick={closeConfirm}>Cancel</button>
            <button className="pc-btn pc-btn-danger" style={{ height: 36, padding: "0 16px", fontSize: 13, borderRadius: 8 }} onClick={confirm.cb}>Delete</button>
          </div>
        </div>
      </div>
    </Fragment>
  );
}